<template>
  <v-card
    class="border mb-4"
    elevation="0"
    data-testid="detailed-impact-panel"
  >
    <v-card-text class="pt-4">
      <section
        v-if="websiteImpact.available"
        class="preview-run-summary mb-6"
        data-testid="preview-website-summary-section"
      >
        <h3 class="text-subtitle-1 font-weight-bold mb-1">Publish preview summary</h3>
        <div class="text-body-2 text-medium-emphasis mb-3">
          What will happen on the live website when you publish.
        </div>

        <div
          class="preview-run-summary-hero"
          data-testid="preview-website-summary-hero"
        >
          <div class="preview-run-summary-count">
            <template v-if="websiteLiveAfterPublishCount != null">
              {{ websiteLiveAfterPublishCount }}
            </template>
            <template v-else>&mdash;</template>
          </div>
          <div>
            <div class="text-h6 font-weight-bold">vehicles will be live on the website after publish</div>
            <div class="text-body-2 text-medium-emphasis">
              Predicted live website outcome for this publish. Only vehicles whose live website state
              will change are counted below.
            </div>
          </div>
        </div>

        <div
          class="preview-run-summary-grid mt-4"
          data-testid="preview-website-summary-grid"
        >
          <div
            v-for="metric in websiteImpactMetrics"
            :key="metric.id"
            class="preview-run-summary-metric"
            :data-testid="`preview-website-summary-${metric.id}`"
          >
            <div
              class="preview-run-summary-metric-value"
              :data-testid="`preview-website-summary-count-${metric.id}`"
            >
              {{ metric.count }}
            </div>
            <div class="preview-run-summary-metric-label">
              {{ metric.label }}
            </div>
            <button
              v-if="metric.count > 0"
              type="button"
              class="preview-run-summary-metric-action"
              :data-testid="`preview-website-summary-action-${metric.id}`"
              @click="openWebsiteImpactDrilldown(metric)"
            >
              View VINs
            </button>
          </div>
        </div>

        <!-- Publish-Flow-style "More info" accordion. Preview-only: shows only
             metadata that actually exists for this preview run (no fabricated
             Publish-Flow values, no Website Refresh / cache keys / feedIntake).
             Defaults to collapsed, matching Publish Flow. -->
        <v-expansion-panels
          variant="accordion"
          class="mt-4"
          data-testid="preview-summary-more-info"
        >
          <v-expansion-panel>
            <v-expansion-panel-title>More info</v-expansion-panel-title>
            <v-expansion-panel-text>
              <div class="preview-run-summary-meta-grid">
                <div
                  v-if="previewSimulationId"
                  class="preview-run-summary-meta-cell preview-run-summary-meta-cell--wide"
                  data-testid="preview-more-info-simulation-id"
                >
                  <div class="text-caption text-medium-emphasis">Preview ID</div>
                  <div class="text-body-2 font-weight-medium">
                    <code class="preview-run-summary-id">{{ previewSimulationId }}</code>
                  </div>
                </div>
                <div
                  v-if="previewGeneratedAt"
                  class="preview-run-summary-meta-cell"
                  data-testid="preview-more-info-generated-at"
                >
                  <div class="text-caption text-medium-emphasis">Generated</div>
                  <div class="text-body-2 font-weight-medium">
                    <DateTimeWithDealerTooltip :value="previewGeneratedAt" />
                  </div>
                </div>
                <div
                  v-if="previewDurationLabel"
                  class="preview-run-summary-meta-cell"
                  data-testid="preview-more-info-duration"
                >
                  <div class="text-caption text-medium-emphasis">Duration</div>
                  <div class="text-body-2 font-weight-medium">{{ previewDurationLabel }}</div>
                </div>
                <div
                  v-if="previewFeedSource"
                  class="preview-run-summary-meta-cell"
                  data-testid="preview-more-info-feed"
                >
                  <div class="text-caption text-medium-emphasis">Feed</div>
                  <div class="text-body-2 font-weight-medium">{{ previewFeedSource }}</div>
                </div>
                <div
                  v-if="previewVinsEvaluatedLabel"
                  class="preview-run-summary-meta-cell"
                  data-testid="preview-more-info-vins-evaluated"
                >
                  <div class="text-caption text-medium-emphasis">VINs evaluated</div>
                  <div class="text-body-2 font-weight-medium">{{ previewVinsEvaluatedLabel }}</div>
                </div>
                <div
                  v-if="previewAccountId"
                  class="preview-run-summary-meta-cell"
                  data-testid="preview-more-info-account"
                >
                  <div class="text-caption text-medium-emphasis">Account</div>
                  <div class="text-body-2 font-weight-medium">{{ previewAccountId }}</div>
                </div>
              </div>

              <!-- Publish-Flow-style "Inventory outcome details" (Feed-applied /
                   Manual / Total). Current artifacts carry the binary origin
                   split, so all three cards render populated with the SAME
                   unified websiteImpact dataset that powers the metric cards,
                   Website Publish Outcome, and the View VINs modal — never a
                   second count source. Legacy artifacts (no scoped split) hide
                   the Feed/Manual cards and show Total only, rather than
                   presenting zeros as authoritative. -->
              <div
                v-if="websiteImpact.available"
                class="preview-run-summary-outcome mt-4"
                data-testid="preview-more-info-outcome"
              >
                <div class="text-subtitle-2 font-weight-bold mb-1">Inventory outcome details</div>
                <div class="text-caption text-medium-emphasis mb-2">
                  Predicted live website changes for this publish, from the unified
                  website-impact dataset that also powers the cards above.
                </div>

                <div class="preview-outcome-grid">
                  <div
                    v-if="previewOutcomeSplitAvailable"
                    class="preview-bucket-card"
                    data-testid="preview-more-info-outcome-feed"
                  >
                    <div class="preview-bucket-card__title">Feed-applied</div>
                    <div class="preview-bucket-card__subtitle">
                      Predicted changes caused by feed/config simulation
                    </div>
                    <div class="preview-bucket-grid">
                      <div
                        v-for="cell in previewInventoryOutcomeFeedCells"
                        :key="cell.key"
                        class="preview-bucket-cell"
                        :data-testid="`preview-more-info-outcome-feed-${cell.key}`"
                      >
                        <div class="text-caption text-medium-emphasis">{{ cell.label }}</div>
                        <div class="text-h6 font-weight-bold">{{ cell.value }}</div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="previewOutcomeSplitAvailable"
                    class="preview-bucket-card"
                    data-testid="preview-more-info-outcome-manual"
                  >
                    <div class="preview-bucket-card__title">Manual</div>
                    <div class="preview-bucket-card__subtitle">
                      Predicted changes caused by manual actions in this publish
                    </div>
                    <div class="preview-bucket-grid">
                      <div
                        v-for="cell in previewInventoryOutcomeManualCells"
                        :key="cell.key"
                        class="preview-bucket-cell"
                        :data-testid="`preview-more-info-outcome-manual-${cell.key}`"
                      >
                        <div class="text-caption text-medium-emphasis">{{ cell.label }}</div>
                        <div class="text-h6 font-weight-bold">{{ cell.value }}</div>
                      </div>
                    </div>
                  </div>

                  <div
                    class="preview-bucket-card preview-bucket-card--emphasize"
                    data-testid="preview-more-info-outcome-total-card"
                  >
                    <div class="preview-bucket-card__title">Total</div>
                    <div class="preview-bucket-card__subtitle">
                      Combined predicted live website changes
                    </div>
                    <div class="preview-bucket-grid">
                      <div
                        v-for="cell in previewInventoryOutcomeTotalCells"
                        :key="cell.key"
                        class="preview-bucket-cell"
                        :data-testid="`preview-more-info-outcome-total-${cell.key}`"
                      >
                        <div class="text-caption text-medium-emphasis">{{ cell.label }}</div>
                        <div class="text-h6 font-weight-bold">{{ cell.value }}</div>
                      </div>
                    </div>
                    <div
                      class="text-caption text-medium-emphasis mt-2"
                      data-testid="preview-more-info-outcome-total-note"
                    >
                      {{ websiteImpact.changedLiveVinCount }}
                      {{ pluralize(websiteImpact.changedLiveVinCount, 'vehicle') }}
                      whose live website state will change (deduped).
                    </div>
                  </div>
                </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </section>

      <section
        class="vin-lookup-panel mb-6"
        data-testid="detailed-impact-vin-section"
      >
        <div>
          <h3 class="text-subtitle-1 font-weight-bold mb-1">Vehicle preview</h3>
          <div class="text-body-2 text-medium-emphasis mb-3">
            Enter a VIN to see how this preview will process that specific vehicle.
          </div>
        </div>

        <div class="d-flex align-center ga-3 flex-wrap">
          <v-text-field
            v-model="vinInput"
            label="VIN"
            variant="outlined"
            density="comfortable"
            hide-details
            maxlength="64"
            autocomplete="off"
            class="flex-grow-1"
            :disabled="vinLookupInFlight"
            data-testid="detailed-impact-vin-input"
            @keydown.enter.prevent="submitVinLookup"
          />
          <v-btn
            color="primary"
            variant="flat"
            :loading="vinLookupInFlight"
            :disabled="vinLookupDisabled"
            data-testid="detailed-impact-vin-submit"
            @click="submitVinLookup"
          >
            Preview vehicle
          </v-btn>
        </div>

        <div
          v-if="detailedImpactStore.vinTrailStatus === 'invalid'"
          class="text-caption text-error mt-2"
          data-testid="detailed-impact-vin-invalid"
        >
          Enter a valid VIN.
        </div>

        <div
          v-else-if="detailedImpactStore.vinTrailStatus === 'loading'"
          class="d-flex align-center ga-2 mt-4"
          data-testid="detailed-impact-vin-loading"
        >
          <v-progress-circular indeterminate color="primary" size="20" />
          <span class="text-body-2">Looking up vehicle...</span>
        </div>

        <v-alert
          v-else-if="detailedImpactStore.vinTrailStatus === 'notFound'"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mt-4"
          data-testid="detailed-impact-vin-not-found"
        >
          No detailed records were found for this VIN in this preview.
        </v-alert>

        <v-alert
          v-else-if="detailedImpactStore.vinTrailStatus === 'unavailable'"
          type="info"
          variant="tonal"
          density="comfortable"
          class="mt-4"
          data-testid="detailed-impact-vin-unavailable"
        >
          We couldn't load this vehicle's details right now. Please try again or come back later.
        </v-alert>
      </section>

      <!-- ===========================
           Per-area summary section
           =========================== -->
      <section class="mb-6" data-testid="detailed-impact-summary-section">
        <h3 class="text-subtitle-1 font-weight-bold mb-1">What happened inside the publish pipeline?</h3>
        <div class="text-body-2 text-medium-emphasis mb-3">
          Step-by-step explanation of how the preview result was calculated.
        </div>

        <div
          v-if="previewHeroSummary"
          class="preview-hero-summary mb-3"
          data-testid="detailed-impact-hero-summary"
        >
          {{ previewHeroSummary }}
        </div>

        <div
          v-if="flowSteps.length > 0"
          class="preview-flow mb-4"
          data-testid="detailed-impact-flow-overview"
        >
          <button
            v-for="step in flowSteps"
            :key="step.id"
            type="button"
            class="preview-flow__step"
            :data-testid="`detailed-impact-flow-step-${step.id}`"
            @click="scrollToPreviewStage(step.stage)"
          >
            <div class="preview-flow__eyebrow">
              Step {{ step.stepNumber }}
            </div>
            <div class="preview-flow__label">
              {{ step.label }}
            </div>
            <div class="preview-flow__metric">
              {{ step.metric }}
            </div>
            <div
              v-if="step.detail"
              class="preview-flow__detail"
            >
              {{ step.detail }}
            </div>
          </button>
        </div>

        <div
          v-if="detailedImpactStore.summaryStatus === 'loading'"
          class="d-flex align-center ga-3"
          data-testid="detailed-impact-summary-loading"
        >
          <v-progress-circular indeterminate color="primary" size="24" />
          <span class="text-body-2">Loading details...</span>
        </div>

        <v-alert
          v-else-if="detailedImpactStore.summaryStatus === 'unavailable'"
          type="info"
          variant="tonal"
          density="comfortable"
          data-testid="detailed-impact-summary-unavailable"
        >
          <div class="d-flex justify-space-between align-center ga-3 flex-wrap">
            <div>
              Detailed impact information isn't available right now. Try again in a moment,
              or run a new preview.
            </div>
            <v-btn
              size="small"
              variant="tonal"
              color="primary"
              data-testid="detailed-impact-summary-retry"
              @click="retrySummary"
            >
              Try again
            </v-btn>
          </div>
        </v-alert>

        <v-expansion-panels
          v-else-if="detailedImpactStore.summaryAvailable && areaRows.length > 0"
          v-model="expandedStagePanels"
          multiple
          variant="accordion"
          class="preview-stage-accordion"
          data-testid="detailed-impact-summary-list"
        >
          <v-expansion-panel
            v-for="row in areaRows"
            :id="`preview-stage-${row.stage}`"
            :key="row.stage"
            :value="String(row.stage)"
            :data-testid="`detailed-impact-summary-row-${row.stage}`"
            class="preview-stage-row"
          >
            <v-expansion-panel-title>
              <div class="d-flex justify-space-between align-center flex-wrap ga-3">
                <div>
                  <div class="text-subtitle-2 font-weight-bold">{{ row.label }}</div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ row.detail }}
                  </div>
                </div>
                <div
                  v-if="row.warningCount > 0"
                  class="d-flex align-center ga-1"
                  :title="row.warningTitle"
                  data-testid="detailed-impact-summary-warning"
                >
                  <v-icon size="small" color="warning">mdi-alert-circle-outline</v-icon>
                  <span class="text-caption font-weight-medium text-warning">
                    {{ row.warningLabel }}
                  </span>
                </div>
              </div>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <div>
                  <div
                    v-if="stagePlainDescription(row.stage)"
                    class="preview-stage-description mt-2"
                  >
                    {{ stagePlainDescription(row.stage) }}
                  </div>

                  <div
                    v-if="stageInputOutput(row).length > 0"
                    class="preview-stage-flow mt-2"
                    :data-testid="`detailed-impact-stage-flow-${row.stage}`"
                  >
                    <div
                      v-for="item in stageInputOutput(row)"
                      :key="item.label"
                      class="preview-stage-flow__item"
                    >
                      <div class="preview-stage-flow__label">
                        {{ item.label }}
                      </div>
                      <div class="preview-stage-flow__value">
                        {{ item.value }}
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="row.stage === 'fieldMapping'"
                    class="mt-3"
                    data-testid="detailed-impact-field-mapping-audit"
                  >
                    <v-alert
                      v-if="fieldMappingAuditSummary?.auditIntegrityIssues"
                      type="error"
                      variant="tonal"
                      density="compact"
                      class="mt-2 mb-2"
                      data-testid="detailed-impact-field-mapping-audit-integrity"
                    >
                      <div class="font-weight-bold">
                        {{ fieldMappingAuditSummary.auditIntegrityIssues }} changed vehicles have no
                        classified field-level reason.
                      </div>
                      <div>
                        This indicates a publish-preview simulation bug — these vehicles were marked
                        as changed but the simulation could not produce a concrete reason. Investigate
                        before trusting the run.
                      </div>
                    </v-alert>
                    <v-alert
                      v-if="fieldMappingAuditSummary?.sourceOnlyChangedVehicles"
                      type="info"
                      variant="tonal"
                      density="compact"
                      class="mt-2 mb-2"
                      data-testid="detailed-impact-field-mapping-source-only"
                    >
                      {{ fieldMappingAuditSummary.sourceOnlyChangedVehicles }} vehicles had data-source changes
                      without a visible value change.
                    </v-alert>

                    <div
                      v-if="feedFieldRulesImpact"
                      class="feed-field-rules-summary mt-3"
                      data-testid="detailed-impact-feed-field-rules-summary"
                    >
                      <div class="d-flex justify-space-between align-start ga-3 flex-wrap">
                        <div>
                          <div class="text-subtitle-2 font-weight-bold">
                            Feed field rules
                          </div>
                          <div class="text-body-2 text-medium-emphasis">
                            {{ feedFieldRulesImpact.detail }}
                          </div>
                          <div class="text-caption text-medium-emphasis mt-1">
                            Used by Vehicle data to transform feed values after mapping.
                          </div>
                        </div>
                        <v-chip
                          size="x-small"
                          color="primary"
                          variant="tonal"
                          data-testid="detailed-impact-feed-field-rules-evaluated"
                        >
                          {{ feedFieldRulesImpact.evaluatedLabel }}
                        </v-chip>
                      </div>

                      <div
                        v-if="feedFieldRulesImpact.fieldSummary"
                        class="text-body-2 mt-2"
                        data-testid="detailed-impact-feed-field-rules-field-summary"
                      >
                        {{ feedFieldRulesImpact.fieldSummary }}
                      </div>

                      <div
                        v-if="feedFieldRulesImpact.actionSummary"
                        class="text-body-2 mt-1"
                        data-testid="detailed-impact-feed-field-rules-action-summary"
                      >
                        {{ feedFieldRulesImpact.actionSummary }}
                      </div>
                    </div>

                    <div
                      class="d-flex align-center ga-2 flex-wrap mt-2 mb-3"
                      role="tablist"
                      aria-label="Vehicle data views"
                    >
                      <v-btn
                        size="small"
                        role="tab"
                        :aria-selected="vehicleDataView === 'byVehicle'"
                        :variant="vehicleDataView === 'byVehicle' ? 'flat' : 'tonal'"
                        color="primary"
                        data-testid="detailed-impact-field-mapping-tab-by-vehicle"
                        @click="vehicleDataView = 'byVehicle'"
                      >
                        By vehicle
                      </v-btn>
                      <v-btn
                        size="small"
                        role="tab"
                        :aria-selected="vehicleDataView === 'byField'"
                        :variant="vehicleDataView === 'byField' ? 'flat' : 'tonal'"
                        color="primary"
                        data-testid="detailed-impact-field-mapping-tab-by-field"
                        @click="vehicleDataView = 'byField'"
                      >
                        By field
                      </v-btn>
                    </div>

                    <div
                      v-if="vehicleDataView === 'byVehicle'"
                      data-testid="detailed-impact-field-mapping-by-vehicle"
                    >
                      <div
                        class="vehicle-data-buckets"
                        data-testid="detailed-impact-field-mapping-vehicle-buckets"
                      >
                        <button
                          type="button"
                          class="vehicle-data-bucket"
                          :class="{ 'vehicle-data-bucket--selected': selectedVehicleBucket === 'changed' }"
                          :aria-pressed="selectedVehicleBucket === 'changed'"
                          data-testid="detailed-impact-field-mapping-bucket-changed"
                          @click="openVehicleDataDrilldown('changed')"
                        >
                          <div class="text-subtitle-2 font-weight-bold">Changed vehicles</div>
                          <div class="text-h6 mt-1">
                            {{ vehicleDataBucketCountLabel('changed') }}
                          </div>
                          <div class="text-body-2 text-medium-emphasis mt-1">
                            {{ vehicleDataBucketDetail('changed') }}
                          </div>
                        </button>

                        <button
                          type="button"
                          class="vehicle-data-bucket"
                          :class="{ 'vehicle-data-bucket--selected': selectedVehicleBucket === 'unchanged' }"
                          :aria-pressed="selectedVehicleBucket === 'unchanged'"
                          data-testid="detailed-impact-field-mapping-bucket-unchanged"
                          @click="openVehicleDataDrilldown('unchanged')"
                        >
                          <div class="text-subtitle-2 font-weight-bold">Unchanged vehicles</div>
                          <div class="text-h6 mt-1">
                            {{ vehicleDataBucketCountLabel('unchanged') }}
                          </div>
                          <div class="text-body-2 text-medium-emphasis mt-1">
                            {{ vehicleDataBucketDetail('unchanged') }}
                          </div>
                        </button>

                        <button
                          type="button"
                          class="vehicle-data-bucket"
                          :class="{ 'vehicle-data-bucket--selected': selectedVehicleBucket === 'emptying' }"
                          data-testid="detailed-impact-field-mapping-bucket-emptying"
                          :aria-pressed="selectedVehicleBucket === 'emptying'"
                          @click="openVehicleDataDrilldown('emptying')"
                        >
                          <div class="text-subtitle-2 font-weight-bold">Values becoming empty</div>
                          <div class="text-h6 mt-1">
                            {{ vehicleDataBucketCountLabel('emptying') }}
                          </div>
                          <div class="text-body-2 text-medium-emphasis mt-1">
                            {{ vehicleDataBucketDetail('emptying') }}
                          </div>
                        </button>
                      </div>

                      <div class="text-body-2 text-medium-emphasis mt-3">
                        Select a count above to open the VIN list and per-VIN field changes.
                      </div>
                    </div>

                    <div
                      v-else
                      data-testid="detailed-impact-field-mapping-by-field"
                    >
                      <v-alert
                        v-if="fieldMappingAuditStatus === 'error'"
                        type="warning"
                        variant="tonal"
                        density="compact"
                        class="mb-2"
                        data-testid="detailed-impact-field-mapping-field-error"
                      >
                        Field value/source details could not be loaded.
                      </v-alert>
                      <v-expansion-panels
                        v-if="fieldMappingFieldSummaryRows.length > 0"
                        v-model="expandedFieldPanels"
                        multiple
                        variant="accordion"
                        class="field-mapping-audit"
                        data-testid="detailed-impact-field-mapping-fields"
                        @update:model-value="handleFieldMappingExpansion"
                      >
                        <v-expansion-panel
                          v-for="fieldRow in fieldMappingFieldSummaryRows"
                          :key="fieldRow.key"
                          :value="fieldRow.field"
                          :data-testid="`detailed-impact-field-mapping-field-${fieldRow.field}`"
                        >
                          <v-expansion-panel-title>
                            <div>
                              <div class="text-subtitle-2 font-weight-bold">
                                {{ fieldRow.label }}
                              </div>
                              <div class="text-body-2 text-medium-emphasis">
                                {{ fieldRow.affectedVehicles }}
                                {{ pluralize(fieldRow.affectedVehicles, 'affected vehicle') }}
                                ·
                                {{ fieldRow.valueChanges }}
                                {{ pluralize(fieldRow.valueChanges, 'value change') }}
                              </div>
                            </div>
                          </v-expansion-panel-title>

                          <v-expansion-panel-text>
                            <div
                              v-if="fieldMappingAuditStatus === 'loading'"
                              class="d-flex align-center ga-2 text-body-2 text-medium-emphasis"
                            >
                              <v-progress-circular indeterminate color="primary" size="16" />
                              <span>Loading value changes...</span>
                            </div>

                            <div
                              v-else-if="(fieldMappingDetailRowsByField.get(fieldRow.field) ?? []).length > 0"
                              class="field-mapping-audit__details"
                            >
                              <div
                                v-for="transition in fieldMappingDetailRowsByField.get(fieldRow.field) ?? []"
                                :key="transition.key"
                                class="field-mapping-audit__section"
                                :data-testid="`detailed-impact-field-mapping-transition-${transition.testId}`"
                              >
                                <div class="field-mapping-audit__title">
                                  {{ transition.beforeLabel }} -> {{ transition.afterLabel }}
                                </div>
                                <div class="text-body-2 text-medium-emphasis">
                                  {{ transition.count }} {{ pluralize(transition.count, 'vehicle') }}
                                  <template v-if="sourceSummaryForTransition(transition)">
                                    · {{ sourceSummaryForTransition(transition) }}
                                  </template>
                                </div>
                              </div>
                            </div>

                            <div
                              v-else
                              class="text-body-2 text-medium-emphasis"
                            >
                              No value-level details were found for this field.
                            </div>
                          </v-expansion-panel-text>
                        </v-expansion-panel>
                      </v-expansion-panels>

                      <div
                        v-else
                        class="text-body-2 text-medium-emphasis"
                      >
                        No field-level vehicle data changes were captured for this preview.
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="row.stage === 'groups' && vehicleGroupsImpact"
                    class="vehicle-groups-drilldown mt-4"
                    data-testid="detailed-impact-vehicle-groups-drilldown"
                  >
                    <div
                      v-if="hasVehicleGroupDrilldownChanges"
                      class="group-bucket-grid mb-4"
                    >
                      <v-btn
                        size="small"
                        variant="tonal"
                        :color="vehicleGroupBucket === 'changed' ? 'primary' : undefined"
                        data-testid="vehicle-groups-bucket-changed"
                        @click="openVehicleGroupDrilldown('changed')"
                      >
                        Changed
                      </v-btn>
                      <v-btn
                        size="small"
                        variant="tonal"
                        :color="vehicleGroupBucket === 'added' ? 'primary' : undefined"
                        data-testid="vehicle-groups-bucket-added"
                        @click="openVehicleGroupDrilldown('added')"
                      >
                        Added to groups
                      </v-btn>
                      <v-btn
                        size="small"
                        variant="tonal"
                        :color="vehicleGroupBucket === 'removed' ? 'primary' : undefined"
                        data-testid="vehicle-groups-bucket-removed"
                        @click="openVehicleGroupDrilldown('removed')"
                      >
                        Removed from groups
                      </v-btn>
                      <v-btn
                        size="small"
                        variant="tonal"
                        :color="vehicleGroupBucket === 'unchanged' ? 'primary' : undefined"
                        data-testid="vehicle-groups-bucket-unchanged"
                        @click="openVehicleGroupDrilldown('unchanged')"
                      >
                        Unchanged
                      </v-btn>
                    </div>

                    <div
                      v-if="hasVehicleGroupDrilldownChanges"
                      class="text-body-2 text-medium-emphasis"
                    >
                      Select a category above to open the VIN list and per-VIN grouping changes.
                    </div>
                  </div>
                  <div
                    v-if="row.stage === 'listingRules' && listingVisibilityImpact"
                    class="listing-visibility-drilldown mt-4"
                    data-testid="detailed-impact-listing-visibility-drilldown"
                  >
                    <div class="listing-visibility-bucket-grid mb-4">
                      <v-btn
                        v-if="listingVisibilityBucketCount('willAppear') > 0"
                        size="small"
                        variant="tonal"
                        :color="listingVisibilityBucket === 'willAppear' ? 'primary' : undefined"
                        data-testid="listing-visibility-bucket-will-appear"
                        @click="openListingVisibilityDrilldown('willAppear')"
                      >
                        {{ visibilityCategoryLabel('willAppear') }}
                      </v-btn>
                      <v-btn
                        v-if="listingVisibilityBucketCount('willHide') > 0"
                        size="small"
                        variant="tonal"
                        :color="listingVisibilityBucket === 'willHide' ? 'primary' : undefined"
                        data-testid="listing-visibility-bucket-will-hide"
                        @click="openListingVisibilityDrilldown('willHide')"
                      >
                        {{ visibilityCategoryLabel('willHide') }}
                      </v-btn>
                      <v-btn
                        v-if="listingVisibilityBucketCount('reasonChanged') > 0"
                        size="small"
                        variant="tonal"
                        :color="listingVisibilityBucket === 'reasonChanged' ? 'primary' : undefined"
                        data-testid="listing-visibility-bucket-reason-changed"
                        @click="openListingVisibilityDrilldown('reasonChanged')"
                      >
                        {{ visibilityCategoryLabel('reasonChanged') }}
                      </v-btn>
                      <v-btn
                        v-if="listingVisibilityBucketCount('unchanged') > 0"
                        size="small"
                        variant="tonal"
                        :color="listingVisibilityBucket === 'unchanged' ? 'primary' : undefined"
                        data-testid="listing-visibility-bucket-unchanged"
                        @click="openListingVisibilityDrilldown('unchanged')"
                      >
                        {{ visibilityCategoryLabel('unchanged') }}
                      </v-btn>
                    </div>

                    <div
                      v-if="!listingVisibilitySelectedBucketAvailable"
                      class="text-body-2 text-medium-emphasis mb-3"
                      data-testid="listing-visibility-unavailable"
                    >
                      VINs are not available for this visibility category.
                    </div>

                    <div
                      v-else
                      class="text-body-2 text-medium-emphasis"
                    >
                      Select a category above to open the VIN list and per-VIN visibility details.
                    </div>
                  </div>
                  <div
                    v-if="row.diagnostics"
                    class="preprocessor-diagnostics text-body-2 mt-3"
                    data-testid="detailed-impact-preprocessor-diagnostics"
                  >
                    <div
                      class="preprocessor-diagnostics__outcome"
                      data-testid="detailed-impact-preprocessor-outcome"
                    >
                      <div class="preprocessor-diagnostics__eyebrow">Result</div>
                      <div class="preprocessor-diagnostics__outcome-title">
                        {{ diagnosticPreviewOutput(row.diagnostics).audit.uniqueVinCount }}
                        {{ pluralize(diagnosticPreviewOutput(row.diagnostics).audit.uniqueVinCount, 'vehicle') }}
                        continue to the next step
                      </div>
                      <div class="text-medium-emphasis">
                        {{ preProcessorOutcomeDetail(row.diagnostics) }}
                      </div>
                    </div>

                    <div class="preprocessor-diagnostics__grid">
                      <div class="preprocessor-diagnostics__section" data-testid="detailed-impact-preprocessor-input-info">
                        <div class="preprocessor-diagnostics__title">Incoming feed</div>
                        <dl class="preprocessor-diagnostics__facts">
                          <div>
                            <dt>Source</dt>
                            <dd>{{ formatFeedSource(diagnosticInput(row.diagnostics).source) }}</dd>
                          </div>
                          <div>
                            <dt>Feed rows</dt>
                            <dd>{{ diagnosticInputAudit(row.diagnostics).rowCount }}</dd>
                          </div>
                          <div>
                            <dt>Valid unique VINs</dt>
                            <dd>{{ diagnosticInputAudit(row.diagnostics).uniqueVinCount }}</dd>
                          </div>
                          <div>
                            <dt>VIN issues</dt>
                            <dd>{{ diagnosticInputVinIssueLabel(row.diagnostics) }}</dd>
                          </div>
                        </dl>

                        <div
                          v-if="diagnosticInputFiles(row.diagnostics).length > 0"
                          class="preprocessor-diagnostics__files"
                        >
                          <div class="preprocessor-diagnostics__file-label">
                            Input file
                          </div>

                          <div
                            v-for="file in diagnosticInputFiles(row.diagnostics)"
                            :key="file.key"
                            class="d-flex align-center ga-2 flex-wrap"
                          >
                            <span>
                              {{ formatFeedFileName(file.fileName) }}
                            </span>
                            <v-btn
                              size="x-small"
                              variant="text"
                              color="primary"
                              prepend-icon="mdi-open-in-new"
                              :loading="openingFeedKey === file.key"
                              data-testid="detailed-impact-preprocessor-input-feed"
                              @click="openFeedFile(file, 'sourceInput')"
                            >
                              Download input file
                            </v-btn>
                          </div>
                        </div>
                        <div
                          v-else
                          class="text-medium-emphasis mt-2"
                        >
                          Raw input file was not captured for this preview.
                        </div>
                      </div>

                      <div class="preprocessor-diagnostics__section" data-testid="detailed-impact-preprocessor-process-info">
                        <div class="preprocessor-diagnostics__title">Filter result</div>
                        <dl class="preprocessor-diagnostics__facts">
                          <div>
                            <dt>Matched filters</dt>
                            <dd>
                              {{ processAudit(row.diagnostics).matchedUniqueVinCount }}
                              {{ pluralize(processAudit(row.diagnostics).matchedUniqueVinCount, 'vehicle') }}
                            </dd>
                          </div>
                          <div>
                            <dt>Filtered out</dt>
                            <dd>
                              {{ preProcessorFilteredOutCount(row.diagnostics) }}
                              {{ pluralize(preProcessorFilteredOutCount(row.diagnostics), 'vehicle') }}
                            </dd>
                          </div>
                          <div>
                            <dt>Matched rows</dt>
                            <dd>{{ processAudit(row.diagnostics).matchedRowCount }}</dd>
                          </div>
                          <div>
                            <dt>Match issues</dt>
                            <dd>{{ processVinIssueLabel(row.diagnostics) }}</dd>
                          </div>
                        </dl>

                        <div
                          v-if="row.diagnostics.filters.length > 0"
                          class="preprocessor-diagnostics__subsection"
                        >
                          <div class="preprocessor-diagnostics__subheading">Rules that matched</div>
                          <div
                            v-for="filter in row.diagnostics.filters"
                            :key="filter.label"
                            class="preprocessor-diagnostics__rule"
                          >
                            <span class="font-weight-medium">
                              {{ filter.label }}:
                            </span>
                            {{ filter.matchCount }} {{ pluralize(filter.matchCount, 'vehicle') }}
                            <span v-if="filter.criteria.length > 0" class="text-medium-emphasis">
                              · {{ filter.criteria.join('; ') }}
                            </span>
                          </div>
                        </div>

                        <div
                          v-if="row.diagnostics.fields.length > 0"
                          class="preprocessor-diagnostics__subsection"
                        >
                          <div class="preprocessor-diagnostics__subheading">Values seen</div>
                          <div
                            v-for="field in row.diagnostics.fields"
                            :key="field.label"
                            class="preprocessor-diagnostics__rule"
                          >
                            <span class="font-weight-medium">
                              {{ field.label }}:
                            </span>
                            {{ formatFieldValues(field.valuesSeen, field.otherCount) }}
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="diagnosticPreviewOutput(row.diagnostics)"
                        class="preprocessor-diagnostics__section"
                        data-testid="detailed-impact-preprocessor-output-info"
                      >
                        <div class="preprocessor-diagnostics__title">Output</div>
                        <dl class="preprocessor-diagnostics__facts">
                          <div>
                            <dt>Vehicles continuing</dt>
                            <dd>{{ diagnosticPreviewOutput(row.diagnostics).audit.uniqueVinCount }}</dd>
                          </div>
                          <div>
                            <dt>Output rows</dt>
                            <dd>{{ diagnosticPreviewOutput(row.diagnostics).audit.rowCount }}</dd>
                          </div>
                          <div>
                            <dt>Output issues</dt>
                            <dd>{{ outputVinIssueLabel(row.diagnostics) }}</dd>
                          </div>
                        </dl>

                        <div
                          v-if="diagnosticOutputFile(row.diagnostics)"
                          class="preprocessor-diagnostics__files"
                        >
                          <div class="preprocessor-diagnostics__file-label">
                            Output file
                          </div>
                          <div class="d-flex align-center ga-2 flex-wrap">
                            <span>
                              {{ formatFeedFileName(diagnosticOutputFile(row.diagnostics)?.fileName) }}
                            </span>
                            <v-btn
                              size="x-small"
                              variant="text"
                              color="primary"
                              prepend-icon="mdi-open-in-new"
                              :loading="openingFeedKey === diagnosticOutputFile(row.diagnostics)?.key"
                              data-testid="detailed-impact-preprocessor-output-feed"
                              @click="openFeedFile(diagnosticOutputFile(row.diagnostics), 'preProcessorOutput')"
                            >
                              Download output CSV
                            </v-btn>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <div
          v-else-if="detailedImpactStore.summaryAvailable && areaRows.length === 0"
          class="text-body-2 text-medium-emphasis"
          data-testid="detailed-impact-summary-empty"
        >
          No per-area details were captured for this preview.
        </div>

      </section>

    </v-card-text>

    <v-dialog
      v-model="vinLookupResultDialog"
      max-width="920"
      data-testid="detailed-impact-vin-lookup-dialog"
      @update:model-value="handleVinLookupResultDialogUpdate"
    >
      <v-card class="vin-preview-result-card">
        <v-card-title class="vin-preview-result-card__header d-flex justify-space-between align-center ga-3">
          <span class="text-h6 font-weight-bold">Vehicle preview result</span>
          <v-btn
            icon
            variant="text"
            size="small"
            aria-label="Close vehicle preview result"
            data-testid="detailed-impact-vin-lookup-close"
            @click="closeVinLookupResultDialog"
          >
            <v-icon icon="mdi-close" />
          </v-btn>
        </v-card-title>

        <v-card-text class="vin-preview-result-card__body">
          <div
            v-if="detailedImpactStore.vinTrailReady && vinTrailRows.length > 0"
            class="vin-lookup-result"
            data-testid="detailed-impact-vin-trail"
          >
            <div class="vin-lookup-result__hero">
              <div>
                <div class="text-caption text-medium-emphasis">Preview trail</div>
                <div class="text-subtitle-1 font-weight-bold">
                  {{ detailedImpactStore.vinTrailQueryEcho }}
                </div>
              </div>
              <v-chip
                size="small"
                color="primary"
                variant="tonal"
              >
                {{ vinTrailRows.length }} {{ pluralize(vinTrailRows.length, 'step') }}
              </v-chip>
            </div>

            <div class="vin-lookup-result__steps mt-4">
              <article
                v-for="row in vinTrailRows"
                :key="row.stage"
                class="vin-lookup-result__step"
                :data-testid="`detailed-impact-vin-row-${row.stage}`"
              >
                <div class="d-flex align-start justify-space-between ga-3 flex-wrap">
                  <div>
                    <div class="text-subtitle-2 font-weight-bold">{{ row.label }}</div>
                  </div>
                  <v-chip
                    size="small"
                    :color="vinTrailStatusColor(row)"
                    variant="tonal"
                  >
                    {{ row.statusLabel }}
                  </v-chip>
                </div>
                <div
                  v-if="row.detail"
                  class="text-body-2 mt-2"
                >
                  {{ row.detail }}
                </div>
                <div
                  v-if="row.changes.length > 0"
                  class="vin-lookup-result__changes mt-3"
                >
                  <div
                    v-for="change in row.changes"
                    :key="change.key"
                    class="vin-lookup-result__change"
                  >
                    <div class="vin-lookup-result__change-label">
                      {{ change.label }}
                    </div>
                    <div class="vin-lookup-result__change-values">
                      <span>{{ change.before }}</span>
                      <v-icon icon="mdi-arrow-right" size="14" />
                      <span>{{ change.after }}</span>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="vinDrilldownDialog"
      max-width="1120"
      data-testid="detailed-impact-vin-drilldown-dialog"
      @update:model-value="handleVinDrilldownDialogUpdate"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center ga-3">
          <span class="text-h6 font-weight-bold">{{ vinDrilldownTitle }}</span>
          <v-btn
            icon
            variant="text"
            size="small"
            aria-label="Close VIN drill-down"
            data-testid="detailed-impact-vin-drilldown-close"
            @click="closeVinDrilldownDialog"
          >
            <v-icon icon="mdi-close" />
          </v-btn>
        </v-card-title>

        <v-card-subtitle>
          {{ vinDrilldownSubtitle }}
        </v-card-subtitle>

        <v-card-text>
          <div class="vin-drilldown-layout">
            <section class="vin-drilldown-list">
              <template v-if="vinDrilldownStage === 'vehicleData'">
                <div class="group-bucket-grid mb-3">
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="selectedVehicleBucket === 'changed' ? 'primary' : undefined"
                    :disabled="isVehicleDataBucketTabDisabled('changed')"
                    data-testid="modal-field-mapping-bucket-changed"
                    @click="setVehicleDataBucket('changed')"
                  >
                    Changed
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="selectedVehicleBucket === 'unchanged' ? 'primary' : undefined"
                    :disabled="isVehicleDataBucketTabDisabled('unchanged')"
                    data-testid="modal-field-mapping-bucket-unchanged"
                    @click="setVehicleDataBucket('unchanged')"
                  >
                    Unchanged
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="selectedVehicleBucket === 'emptying' ? 'primary' : undefined"
                    :disabled="isVehicleDataBucketTabDisabled('emptying')"
                    data-testid="modal-field-mapping-bucket-emptying"
                    @click="setVehicleDataBucket('emptying')"
                  >
                    Values becoming empty
                  </v-btn>
                </div>

                <div class="text-body-2 text-medium-emphasis mb-2">
                  {{ selectedVehicleBucket ? vehicleDataBucketListHelp(selectedVehicleBucket) : 'Select a category to load VINs.' }}
                </div>

                <div class="d-flex align-end ga-2 flex-wrap mb-3">
                  <v-text-field
                    v-model="vehicleDataSearch"
                    label="Search VIN"
                    variant="outlined"
                    density="compact"
                    hide-details
                    autocomplete="off"
                    class="flex-grow-1"
                    data-testid="detailed-impact-field-mapping-vehicle-search"
                  />
                </div>

                <div class="d-flex align-center justify-space-between ga-3 flex-wrap mb-2">
                  <div class="text-caption text-medium-emphasis">
                    {{ vehicleDataPageRangeLabel }}
                  </div>
                  <div class="d-flex align-center ga-2 flex-shrink-0">
                    <v-btn
                      size="small"
                      variant="tonal"
                      :disabled="vehicleDataPage <= 1 || vehicleDataBucketStatus === 'loading'"
                      data-testid="detailed-impact-field-mapping-vehicle-previous"
                      @click="goToVehicleDataPage(-1)"
                    >
                      Previous
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="tonal"
                      :disabled="!vehicleDataNextCursor || vehicleDataBucketStatus === 'loading'"
                      data-testid="detailed-impact-field-mapping-vehicle-next"
                      @click="goToVehicleDataPage(1)"
                    >
                      Next
                    </v-btn>
                  </div>
                </div>

                <div
                  v-if="vehicleDataBucketStatus === 'loading'"
                  class="d-flex align-center ga-2 text-body-2 text-medium-emphasis"
                  data-testid="detailed-impact-field-mapping-vehicle-loading"
                >
                  <v-progress-circular indeterminate color="primary" size="16" />
                  <span>Loading VINs...</span>
                </div>

                <v-alert
                  v-else-if="vehicleDataBucketStatus === 'error'"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  data-testid="detailed-impact-field-mapping-vehicle-error"
                >
                  Vehicle list could not be loaded.
                </v-alert>

                <div
                  v-else-if="paginatedVehicleBucketRows.length > 0"
                  class="vehicle-data-vin-list"
                >
                  <button
                    v-for="vehicleRow in paginatedVehicleBucketRows"
                    :key="vehicleRow.vin"
                    type="button"
                    class="vehicle-data-vin-row"
                    :class="{ 'vehicle-data-vin-row--selected': selectedVehicleDataVin === vehicleRow.vin }"
                    :data-testid="`detailed-impact-field-mapping-vehicle-row-${vehicleRow.vin}`"
                    @click="selectVehicleDataVin(vehicleRow.vin)"
                  >
                    <span class="font-weight-bold">{{ vehicleRow.vin }}</span>
                    <span
                      v-if="selectedVehicleBucket === 'changed'"
                      class="text-body-2 text-medium-emphasis"
                    >
                      {{ vehicleRow.changedFieldCount ?? 0 }}
                      {{ pluralize(vehicleRow.changedFieldCount ?? 0, 'field') }} affected
                    </span>
                    <span
                      v-else-if="selectedVehicleBucket === 'emptying'"
                      class="text-body-2 text-medium-emphasis"
                    >
                      {{ vehicleRow.emptyingFields?.length ?? 0 }}
                      {{ pluralize(vehicleRow.emptyingFields?.length ?? 0, 'field') }} cleared
                    </span>
                    <span
                      v-else
                      class="text-body-2 text-medium-emphasis"
                    >
                      No data changes
                    </span>
                  </button>
                </div>

                <div
                  v-else
                  class="text-body-2 text-medium-emphasis"
                  data-testid="detailed-impact-field-mapping-vehicle-empty"
                >
                  {{ vehicleDataEmptyMessage }}
                </div>
              </template>

              <template v-else-if="vinDrilldownStage === 'groups'">
                <div class="group-bucket-grid mb-3">
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="vehicleGroupBucket === 'changed' ? 'primary' : undefined"
                    :disabled="isVehicleGroupBucketTabDisabled('changed')"
                    data-testid="vehicle-groups-bucket-changed"
                    @click="setVehicleGroupBucket('changed')"
                  >
                    Changed
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="vehicleGroupBucket === 'added' ? 'primary' : undefined"
                    :disabled="isVehicleGroupBucketTabDisabled('added')"
                    data-testid="vehicle-groups-bucket-added"
                    @click="setVehicleGroupBucket('added')"
                  >
                    Added
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="vehicleGroupBucket === 'removed' ? 'primary' : undefined"
                    :disabled="isVehicleGroupBucketTabDisabled('removed')"
                    data-testid="vehicle-groups-bucket-removed"
                    @click="setVehicleGroupBucket('removed')"
                  >
                    Removed
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="vehicleGroupBucket === 'unchanged' ? 'primary' : undefined"
                    :disabled="isVehicleGroupBucketTabDisabled('unchanged')"
                    data-testid="vehicle-groups-bucket-unchanged"
                    @click="setVehicleGroupBucket('unchanged')"
                  >
                    Unchanged
                  </v-btn>
                </div>

                <div class="text-body-2 text-medium-emphasis mb-2">
                  {{ vehicleGroupBucketListHelp(vehicleGroupBucket) }}
                </div>

                <div class="d-flex align-end ga-2 flex-wrap mb-3">
                  <v-text-field
                    v-model="vehicleGroupSearch"
                    label="Search VIN or group"
                    variant="outlined"
                    density="compact"
                    hide-details
                    autocomplete="off"
                    class="flex-grow-1"
                    data-testid="vehicle-groups-search"
                    @keydown.enter="searchVehicleGroups"
                  />
                </div>

                <div class="d-flex align-center justify-space-between ga-3 flex-wrap mb-2">
                  <div class="text-caption text-medium-emphasis">
                    {{ vehicleGroupRangeLabel }}
                  </div>
                  <div class="d-flex align-center ga-2 flex-shrink-0">
                    <v-btn
                      size="small"
                      variant="tonal"
                      :disabled="vehicleGroupPage <= 1 || vehicleGroupLoading"
                      data-testid="vehicle-groups-previous"
                      @click="goToVehicleGroupPage(-1)"
                    >
                      Previous
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="tonal"
                      :disabled="!vehicleGroupNextCursor || vehicleGroupLoading"
                      data-testid="vehicle-groups-next"
                      @click="goToVehicleGroupPage(1)"
                    >
                      Next
                    </v-btn>
                  </div>
                </div>

                <v-alert
                  v-if="vehicleGroupError"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                  data-testid="vehicle-groups-error"
                >
                  {{ vehicleGroupError }}
                </v-alert>

                <div
                  v-if="vehicleGroupLoading"
                  class="d-flex align-center ga-2 text-body-2 text-medium-emphasis mb-3"
                >
                  <v-progress-circular indeterminate color="primary" size="16" />
                  <span>Loading VINs...</span>
                </div>

                <div
                  v-else-if="!vehicleGroupError && vehicleGroupRows.length === 0"
                  class="text-body-2 text-medium-emphasis mb-3"
                  data-testid="vehicle-groups-empty"
                >
                  {{ vehicleGroupEmptyMessage }}
                </div>

                <div
                  v-else-if="!vehicleGroupError"
                  class="vehicle-data-vin-list"
                >
                  <button
                    v-for="rowItem in vehicleGroupRows"
                    :key="rowItem.vin"
                    type="button"
                    class="vehicle-data-vin-row"
                    :class="{ 'vehicle-data-vin-row--selected': selectedVehicleGroupVin === rowItem.vin }"
                    data-testid="vehicle-group-row"
                    @click="selectVehicleGroupVin(rowItem.vin)"
                  >
                    <span>
                      <span class="font-weight-bold d-block">{{ rowItem.vin }}</span>
                      <span class="text-body-2 text-medium-emphasis">
                        <template v-if="rowItem.addedGroupCount">
                          Added to {{ rowItem.groupNames?.join(', ') || 'vehicle groupings' }}
                        </template>
                        <template v-if="rowItem.addedGroupCount && rowItem.removedGroupCount">
                          ·
                        </template>
                        <template v-if="rowItem.removedGroupCount">
                          Removed from {{ rowItem.groupNames?.join(', ') || 'vehicle groupings' }}
                        </template>
                      </span>
                    </span>
                  </button>
                </div>
              </template>

              <template v-else-if="vinDrilldownStage === 'websiteImpact'">
                <!-- Publish-Flow-style single-category list: NO legacy visibility
                     tabs. The list is pinned to the clicked websiteImpact
                     category so its count/VINs match the summary card. -->
                <div class="d-flex align-end ga-2 flex-wrap mb-3">
                  <v-text-field
                    v-model="listingVisibilitySearch"
                    label="Search VIN"
                    variant="outlined"
                    density="compact"
                    hide-details
                    autocomplete="off"
                    class="flex-grow-1"
                    data-testid="website-impact-search"
                    @keydown.enter="searchListingVisibility"
                  />
                </div>

                <div class="d-flex align-center justify-space-between ga-3 flex-wrap mb-2">
                  <div
                    class="text-caption text-medium-emphasis"
                    data-testid="website-impact-range"
                  >
                    {{ listingVisibilityRangeLabel }}
                  </div>
                  <div class="d-flex align-center ga-2 flex-shrink-0">
                    <v-btn
                      size="small"
                      variant="tonal"
                      :disabled="listingVisibilityPage <= 1 || listingVisibilityLoading"
                      data-testid="website-impact-previous"
                      @click="goToListingVisibilityPage(-1)"
                    >
                      Previous
                    </v-btn>
                    <v-btn
                      size="small"
                      variant="tonal"
                      :disabled="!listingVisibilityNextCursor || listingVisibilityLoading"
                      data-testid="website-impact-next"
                      @click="goToListingVisibilityPage(1)"
                    >
                      Next
                    </v-btn>
                  </div>
                </div>

                <v-alert
                  v-if="listingVisibilityError"
                  type="warning"
                  variant="tonal"
                  density="compact"
                  class="mb-3"
                  data-testid="website-impact-error"
                >
                  {{ listingVisibilityError }}
                </v-alert>

                <div
                  v-if="listingVisibilityLoading"
                  class="d-flex align-center ga-2 text-body-2 text-medium-emphasis mb-3"
                >
                  <v-progress-circular indeterminate color="primary" size="16" />
                  <span>Loading VINs...</span>
                </div>

                <div
                  v-else-if="!listingVisibilityError && listingVisibilityRows.length === 0"
                  class="text-body-2 text-medium-emphasis mb-3"
                  data-testid="website-impact-empty"
                >
                  No vehicles fall into this category for this preview.
                </div>

                <div
                  v-else-if="!listingVisibilityError"
                  class="vehicle-data-vin-list"
                  data-testid="website-impact-vin-list"
                >
                  <button
                    v-for="rowItem in listingVisibilityRows"
                    :key="rowItem.vin"
                    type="button"
                    class="vehicle-data-vin-row"
                    :class="{ 'vehicle-data-vin-row--selected': selectedListingVisibilityVin === rowItem.vin }"
                    data-testid="website-impact-row"
                    @click="selectWebsiteImpactVin(rowItem.vin)"
                  >
                    <span>
                      <span class="font-weight-bold d-block">{{ rowItem.vin }}</span>
                      <span class="text-body-2 text-medium-emphasis">
                        {{ websiteImpactRowSubtitle(rowItem) }}
                      </span>
                    </span>
                  </button>
                </div>
              </template>

              <template v-else-if="vinDrilldownStage === 'visibility'">
                <div class="listing-visibility-bucket-grid mb-3">
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="listingVisibilityBucket === 'willAppear' ? 'primary' : undefined"
                    :disabled="isListingVisibilityBucketTabDisabled('willAppear')"
                    data-testid="listing-visibility-bucket-will-appear"
                    @click="setListingVisibilityBucket('willAppear')"
                  >
                    {{ visibilityCategoryLabel('willAppear') }}
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="listingVisibilityBucket === 'willHide' ? 'primary' : undefined"
                    :disabled="isListingVisibilityBucketTabDisabled('willHide')"
                    data-testid="listing-visibility-bucket-will-hide"
                    @click="setListingVisibilityBucket('willHide')"
                  >
                    {{ visibilityCategoryLabel('willHide') }}
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="listingVisibilityBucket === 'reasonChanged' ? 'primary' : undefined"
                    :disabled="isListingVisibilityBucketTabDisabled('reasonChanged')"
                    data-testid="listing-visibility-bucket-reason-changed"
                    @click="setListingVisibilityBucket('reasonChanged')"
                  >
                    {{ visibilityCategoryLabel('reasonChanged') }}
                  </v-btn>
                  <v-btn
                    size="small"
                    variant="tonal"
                    :color="listingVisibilityBucket === 'unchanged' ? 'primary' : undefined"
                    :disabled="isListingVisibilityBucketTabDisabled('unchanged')"
                    data-testid="listing-visibility-bucket-unchanged"
                    @click="setListingVisibilityBucket('unchanged')"
                  >
                    {{ visibilityCategoryLabel('unchanged') }}
                  </v-btn>
                </div>

                <div
                  v-if="!listingVisibilitySelectedBucketAvailable"
                  class="text-body-2 text-medium-emphasis mb-3"
                  data-testid="listing-visibility-unavailable"
                >
                  VINs are not available for this visibility category.
                </div>

                <template v-else>
                  <div class="d-flex align-end ga-2 flex-wrap mb-3">
                    <v-text-field
                      v-model="listingVisibilitySearch"
                      label="Search VIN or reason"
                      variant="outlined"
                      density="compact"
                      hide-details
                      autocomplete="off"
                      class="flex-grow-1"
                      data-testid="listing-visibility-search"
                      @keydown.enter="searchListingVisibility"
                    />
                  </div>

                  <div class="d-flex align-center justify-space-between ga-3 flex-wrap mb-2">
                    <div class="text-caption text-medium-emphasis">
                      {{ listingVisibilityRangeLabel }}
                    </div>
                    <div class="d-flex align-center ga-2 flex-shrink-0">
                      <v-btn
                        size="small"
                        variant="tonal"
                        :disabled="listingVisibilityPage <= 1 || listingVisibilityLoading"
                        data-testid="listing-visibility-previous"
                        @click="goToListingVisibilityPage(-1)"
                      >
                        Previous
                      </v-btn>
                      <v-btn
                        size="small"
                        variant="tonal"
                        :disabled="!listingVisibilityNextCursor || listingVisibilityLoading"
                        data-testid="listing-visibility-next"
                        @click="goToListingVisibilityPage(1)"
                      >
                        Next
                      </v-btn>
                    </div>
                  </div>

                  <v-alert
                    v-if="listingVisibilityError"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-3"
                    data-testid="listing-visibility-error"
                  >
                    {{ listingVisibilityError }}
                  </v-alert>

                  <div
                    v-if="listingVisibilityLoading"
                    class="d-flex align-center ga-2 text-body-2 text-medium-emphasis mb-3"
                  >
                    <v-progress-circular indeterminate color="primary" size="16" />
                    <span>Loading VINs...</span>
                  </div>

                  <div
                    v-else-if="!listingVisibilityError && listingVisibilityRows.length === 0"
                    class="text-body-2 text-medium-emphasis mb-3"
                    data-testid="listing-visibility-empty"
                  >
                    {{ listingVisibilityEmptyMessage }}
                  </div>

                  <div
                    v-else-if="!listingVisibilityError"
                    class="vehicle-data-vin-list"
                  >
                    <button
                      v-for="rowItem in listingVisibilityRows"
                      :key="rowItem.vin"
                      type="button"
                      class="vehicle-data-vin-row"
                      :class="{ 'vehicle-data-vin-row--selected': selectedListingVisibilityVin === rowItem.vin }"
                      data-testid="listing-visibility-row"
                      @click="selectListingVisibilityVin(rowItem.vin)"
                    >
                      <span>
                        <span class="font-weight-bold d-block">{{ rowItem.vin }}</span>
                        <span
                          v-if="listingVisibilityRowSubtitle(rowItem)"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ listingVisibilityRowSubtitle(rowItem) }}
                        </span>
                      </span>
                    </button>
                  </div>
                </template>
              </template>
            </section>

            <section class="vin-drilldown-detail">
              <template v-if="vinDrilldownStage === 'vehicleData'">
                <div
                  v-if="!selectedVehicleDataVin"
                  class="text-body-2 text-medium-emphasis"
                  data-testid="vin-drilldown-empty-detail"
                >
                  Select a VIN to see its field changes.
                </div>

                <div
                  v-else
                  class="vehicle-data-vin-detail"
                  data-testid="detailed-impact-field-mapping-vehicle-detail"
                >
                  <div class="text-subtitle-2 font-weight-bold">
                    VIN {{ selectedVehicleDataVin }}
                  </div>

                  <div
                    v-if="vehicleDataVinDetailStatus === 'loading'"
                    class="d-flex align-center ga-2 text-body-2 text-medium-emphasis mt-2"
                    data-testid="detailed-impact-field-mapping-vehicle-detail-loading"
                  >
                    <v-progress-circular indeterminate color="primary" size="16" />
                    <span>Loading vehicle details...</span>
                  </div>

                  <v-alert
                    v-else-if="vehicleDataVinDetailStatus === 'error'"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                    data-testid="detailed-impact-field-mapping-vehicle-detail-error"
                  >
                    Vehicle details could not be loaded.
                  </v-alert>

                  <v-alert
                    v-else-if="vehicleDataVinDetailStatus === 'notFound'"
                    type="info"
                    variant="tonal"
                    density="compact"
                    class="mt-2"
                    data-testid="detailed-impact-field-mapping-vehicle-detail-not-found"
                  >
                    No vehicle data changes were found for this VIN.
                  </v-alert>

                  <div
                    v-else-if="selectedVehicleDataVinDetails.length > 0"
                    class="mt-2"
                  >
                    <div
                      v-for="detail in selectedVehicleDataVinDetails"
                      :key="detail.key"
                      class="field-mapping-audit__section"
                      :data-testid="`detailed-impact-field-mapping-vin-detail-${detail.testId}`"
                    >
                      <div class="field-mapping-audit__title">
                        {{ detail.label }}
                      </div>
                      <template v-if="detail.valueChanged">
                        <div class="text-body-2">
                          Previous: {{ detail.previousValue }}
                        </div>
                        <div class="text-body-2">
                          After publish: {{ detail.newValue }}
                        </div>
                      </template>
                      <div
                        v-else
                        class="text-body-2"
                      >
                        Value unchanged after publish
                      </div>
                      <div
                        v-if="sourceSummaryForVinField(detail)"
                        class="text-body-2 text-medium-emphasis"
                      >
                        {{ sourceSummaryForVinField(detail) }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-else
                    class="text-body-2 text-medium-emphasis mt-2"
                  >
                    No field-level data changes were found for this VIN.
                  </div>
                </div>
              </template>

              <template v-else-if="vinDrilldownStage === 'groups'">
                <div
                  v-if="!selectedVehicleGroupVin"
                  class="text-body-2 text-medium-emphasis"
                  data-testid="vin-drilldown-empty-detail"
                >
                  Select a VIN to see its grouping changes.
                </div>

                <div
                  v-else
                  class="vehicle-group-detail"
                  data-testid="vehicle-group-detail"
                >
                  <div class="text-subtitle-2 mb-2">
                    {{ selectedVehicleGroupVin }}
                  </div>

                  <div
                    v-if="vehicleGroupVinDetailLoading"
                    class="d-flex align-center ga-2 text-body-2 text-medium-emphasis"
                  >
                    <v-progress-circular indeterminate color="primary" size="16" />
                    <span>Loading group details...</span>
                  </div>

                  <v-alert
                    v-else-if="vehicleGroupVinDetailError"
                    type="warning"
                    variant="tonal"
                    density="compact"
                  >
                    {{ vehicleGroupVinDetailError }}
                  </v-alert>

                  <template v-else-if="vehicleGroupVinDetail">
                    <div class="group-detail-section">
                      <div class="font-weight-medium mb-1">Added groups</div>
                      <div class="text-body-2 text-medium-emphasis">
                        {{ vehicleGroupVinDetail.added.map((group) => group.groupName).join(', ') || 'None' }}
                      </div>
                    </div>
                    <div class="group-detail-section mt-3">
                      <div class="font-weight-medium mb-1">Removed groups</div>
                      <div class="text-body-2 text-medium-emphasis">
                        {{ vehicleGroupVinDetail.removed.map((group) => group.groupName).join(', ') || 'None' }}
                      </div>
                    </div>
                    <div class="group-detail-section mt-3">
                      <div class="font-weight-medium mb-1">Before publish groups</div>
                      <div class="text-body-2 text-medium-emphasis">
                        {{ vehicleGroupVinDetail.publishedGroups.map((group) => group.groupName).join(', ') || 'None' }}
                      </div>
                    </div>
                    <div class="group-detail-section mt-3">
                      <div class="font-weight-medium mb-1">After publish groups</div>
                      <div class="text-body-2 text-medium-emphasis">
                        {{ vehicleGroupVinDetail.afterPublishGroups.map((group) => group.groupName).join(', ') || 'None' }}
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <template v-else-if="vinDrilldownStage === 'websiteImpact'">
                <div
                  v-if="!selectedListingVisibilityVin"
                  class="text-body-2 text-medium-emphasis"
                  data-testid="vin-drilldown-empty-detail"
                >
                  Select a VIN to review this website change.
                </div>

                <div
                  v-else
                  class="listing-visibility-detail"
                  data-testid="website-impact-detail"
                >
                  <div class="text-subtitle-2 mb-2" data-testid="website-impact-detail-vin">
                    {{ selectedListingVisibilityVin }}
                  </div>

                  <!-- updatedOnWebsite: the listing stays live, so visibility is
                       only eligibility context, NOT a change. Show a muted
                       one-liner instead of a prominent before → after card so
                       the modal reads as a data-change drilldown. -->
                  <div
                    v-if="websiteImpactDrilldownCategory === 'updatedOnWebsite'"
                    class="text-caption text-medium-emphasis mb-2"
                    data-testid="website-impact-detail-visibility-context"
                  >
                    Listing remains visible online.
                  </div>

                  <!-- Visibility categories keep the full before → after card. -->
                  <div
                    v-else
                    class="listing-visibility-detail-card"
                  >
                    <div class="font-weight-medium">Website state</div>
                    <div
                      class="text-body-2 text-medium-emphasis"
                      data-testid="website-impact-detail-website-state"
                    >
                      {{ visibilityDecisionLabel(selectedWebsiteImpactRow?.beforeDecision) }} before publish
                      &rarr;
                      {{ visibilityDecisionLabel(selectedWebsiteImpactRow?.afterDecision) }} after publish
                    </div>
                  </div>

                  <!-- "Updated on website" focuses on the data changes, not
                       visibility-rule explanations. -->
                  <template v-if="websiteImpactDrilldownCategory === 'updatedOnWebsite'">
                    <div
                      v-if="vehicleDataVinDetailStatus === 'loading'"
                      class="d-flex align-center ga-2 text-body-2 text-medium-emphasis mt-2"
                    >
                      <v-progress-circular indeterminate color="primary" size="16" />
                      <span>Loading data changes...</span>
                    </div>

                    <v-alert
                      v-else-if="vehicleDataVinDetailStatus === 'error'"
                      type="warning"
                      variant="tonal"
                      density="compact"
                      class="mt-2"
                    >
                      Website-affecting data changes could not be loaded for this VIN.
                    </v-alert>

                    <div
                      v-else-if="selectedVehicleDataVinDetails.length > 0"
                      class="mt-2"
                      data-testid="website-impact-updated-fields"
                    >
                      <div class="font-weight-medium mb-1">Updated fields</div>
                      <div
                        v-for="detail in selectedVehicleDataVinDetails"
                        :key="detail.key"
                        class="field-mapping-audit__section"
                        :data-testid="`website-impact-updated-field-${detail.testId}`"
                      >
                        <div class="field-mapping-audit__title">
                          {{ detail.label }}
                        </div>
                        <template v-if="detail.valueChanged">
                          <div class="text-body-2">
                            Previous: {{ detail.previousValue }}
                          </div>
                          <div class="text-body-2">
                            After publish: {{ detail.newValue }}
                          </div>
                        </template>
                        <div v-else class="text-body-2">
                          Value unchanged after publish
                        </div>
                        <div
                          v-if="sourceSummaryForVinField(detail)"
                          class="text-body-2 text-medium-emphasis"
                        >
                          {{ sourceSummaryForVinField(detail) }}
                        </div>
                      </div>
                    </div>

                    <div
                      v-else
                      class="text-body-2 text-medium-emphasis mt-2"
                      data-testid="website-impact-updated-fields-empty"
                    >
                      No field-level data changes were captured for this VIN.
                    </div>
                  </template>

                  <!-- Added / removed / made visible / hidden: visibility
                       transition reasons are appropriate here. -->
                  <template v-else>
                    <div
                      v-if="listingVisibilityVinDetailLoading"
                      class="d-flex align-center ga-2 text-body-2 text-medium-emphasis mt-2"
                    >
                      <v-progress-circular indeterminate color="primary" size="16" />
                      <span>Loading details...</span>
                    </div>

                    <v-alert
                      v-else-if="listingVisibilityVinDetailError"
                      type="warning"
                      variant="tonal"
                      density="compact"
                      class="mt-2"
                    >
                      {{ listingVisibilityVinDetailError }}
                    </v-alert>

                    <div
                      v-else-if="listingVisibilityVinDetail && listingVisibilityVinDetail.reasons.length > 0"
                      class="listing-visibility-detail-card mt-2"
                      data-testid="website-impact-detail-reasons"
                    >
                      <div class="font-weight-medium">Why this will happen</div>
                      <div
                        v-for="reason in listingVisibilityVinDetail.reasons"
                        :key="reason.explanation"
                        class="text-body-2 text-medium-emphasis"
                      >
                        {{ reason.explanation }}
                      </div>
                    </div>
                  </template>
                </div>
              </template>

              <template v-else-if="vinDrilldownStage === 'visibility'">
                <div
                  v-if="!selectedListingVisibilityVin"
                  class="text-body-2 text-medium-emphasis"
                  data-testid="vin-drilldown-empty-detail"
                >
                  Select a VIN to see its visibility decision.
                </div>

                <div
                  v-else
                  class="listing-visibility-detail"
                  data-testid="listing-visibility-detail"
                >
                  <div class="text-subtitle-2 mb-2">
                    {{ selectedListingVisibilityVin }}
                  </div>

                  <div
                    v-if="listingVisibilityVinDetailLoading"
                    class="d-flex align-center ga-2 text-body-2 text-medium-emphasis"
                  >
                    <v-progress-circular indeterminate color="primary" size="16" />
                    <span>Loading visibility details...</span>
                  </div>

                  <v-alert
                    v-else-if="listingVisibilityVinDetailError"
                    type="warning"
                    variant="tonal"
                    density="compact"
                  >
                    {{ listingVisibilityVinDetailError }}
                  </v-alert>

                  <template v-else-if="listingVisibilityVinDetail">
                    <div class="listing-visibility-detail-card">
                      <div class="font-weight-medium">Website visibility</div>
                      <div class="text-body-2 text-medium-emphasis">
                        Before publish: {{ visibilityDecisionLabel(listingVisibilityVinDetail.beforeDecision) }}
                      </div>
                      <div class="text-body-2 text-medium-emphasis">
                        After publish: {{ visibilityDecisionLabel(listingVisibilityVinDetail.afterDecision) }}
                      </div>
                    </div>

                    <div
                      v-if="listingVisibilityVinDetail.matchedRuleBefore || listingVisibilityVinDetail.matchedRuleAfter"
                      class="listing-visibility-detail-card mt-2"
                    >
                      <div class="font-weight-medium">
                        {{ listingVisibilityRuleCardTitle(listingVisibilityVinDetail) }}
                      </div>
                      <div class="text-body-2">
                        {{ listingMatchedRuleSummary(listingVisibilityVinDetail.matchedRuleAfter || listingVisibilityVinDetail.matchedRuleBefore) }}
                      </div>
                    </div>

                    <div
                      v-if="listingVisibilityVinDetail.reasons.length > 0"
                      class="listing-visibility-detail-card mt-2"
                    >
                      <div class="font-weight-medium">Explanation</div>
                      <div
                        v-for="reason in listingVisibilityVinDetail.reasons"
                        :key="reason.explanation"
                        class="text-body-2 text-medium-emphasis"
                      >
                        {{ reason.explanation }}
                      </div>
                    </div>
                  </template>
                </div>
              </template>
            </section>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-card>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useDetailedImpactStore } from '@/stores/useDetailedImpactStore'
import { usePublishPreviewStore } from '@/stores/usePublishPreviewStore'
import DateTimeWithDealerTooltip from '@/components/DateTimeWithDealerTooltip.vue'
import {
  getImportFeedFilePresign,
  tryGetPublishPreviewListingVisibilityBucket,
  tryGetPublishPreviewListingVisibilityVinDetail,
  tryGetPublishPreviewStageDecisions,
  tryGetPublishPreviewVehicleDataBucket,
  tryGetPublishPreviewVehicleDataVinDetail,
  tryGetPublishPreviewVehicleGroupBucket,
  tryGetPublishPreviewVehicleGroupVinDetail,
} from '@/services/publishPreviewArtifactsService'
import {
  friendlyAvailabilityCopy,
  friendlyDecisionStatus,
  friendlyFeedFieldRulesPreviewDetail,
  friendlyInventorySyncPreviewDetail,
  friendlyListingRulesPreviewDetail,
  friendlyPreProcessorPreviewDetail,
  friendlyStageLabel,
  websiteImpactCategoryDetail,
  websiteImpactCategoryLabel,
} from '@/utils/publishPreviewStageLabels'
import {
  buildPublishImpactPreviewViewModel,
  isVisibilityBucketAvailable,
  visibilityBucketCount,
} from '@/utils/publishImpactPreviewViewModel'
import type {
  InventoryVisibilityImpact,
  PublishImpactPreviewViewModel,
  WebsiteImpactCategory,
} from '@/utils/publishImpactPreviewViewModel'
import type {
  PublishDecisionRecord,
  PublishFieldMappingAuditSummary,
  PublishListingVisibilityBucket,
  PublishListingVisibilityBucketVinRow,
  PublishListingVisibilityMatchedRuleDetail,
  PublishListingVisibilityVinDetail,
  PublishObservabilitySummary,
  PublishVehicleDataBucketVinRow,
  PublishVehicleDataVinDetail,
  PublishVehicleDataVinFieldDetail,
  PublishVehicleGroupBucket,
  PublishVehicleGroupBucketVinRow,
  PublishVehicleGroupVinDetail,
  PublishVinPipelineTrailChange,
  PublishVinPipelineTrailStage,
  PublishObservedStage,
  PublishPreProcessorDiagnostics,
  PublishPreProcessorFeedFileReference,
  PublishPreProcessorFieldValueCount,
  PublishStageAvailability,
  PublishStageSummary,
} from '@/services/publishPreviewArtifactsService'

const props = withDefaults(defineProps<{
  active?: boolean
}>(), {
  active: true,
})

const previewStore = usePublishPreviewStore()
const accountSettings = useAccountSettingsStore()
const detailedImpactStore = useDetailedImpactStore()

const endpoint = computed(() => import.meta.env.VITE_ISE_BACKEND_URL as string)
const simulationId = computed(() => previewStore.result?.simulationId ?? null)

type VehicleDataView = 'byVehicle' | 'byField'
type VehicleDataBucket = 'changed' | 'unchanged' | 'emptying'
// `websiteImpact` is the Publish-Flow-style five-category drilldown (added /
// removed / updated / made visible / hidden). It reads ONLY the unified
// websiteImpact bucket for the clicked category and never shows the legacy
// visibility tabs. `visibility` remains for legacy / stage-specific drilldowns.
type VinDrilldownStage = 'vehicleData' | 'groups' | 'visibility' | 'websiteImpact'

type PreviewFlowStep = {
  id: string
  stepNumber: number
  stage: PublishObservedStage | string
  label: string
  metric: string
  detail?: string
}

type StageInputOutput = {
  label: 'Input' | 'Output'
  value: string
}

const VEHICLE_DATA_PAGE_SIZE = 25
const VEHICLE_GROUP_PAGE_SIZE = 50
const LISTING_VISIBILITY_PAGE_SIZE = 50
const VEHICLE_GROUP_BUCKET_ORDER: PublishVehicleGroupBucket[] = ['changed', 'added', 'removed', 'unchanged']
const LISTING_VISIBILITY_BUCKET_ORDER: PublishListingVisibilityBucket[] = [
  'willAppear',
  'willHide',
  'reasonChanged',
  'unchanged',
]

const vinInput = ref('')
const vinLookupResultDialog = ref(false)
const openingFeedKey = ref('')
const expandedStagePanels = ref<string[]>([])
const fieldMappingAuditStatus = ref<'idle' | 'loading' | 'ok' | 'error'>('idle')
const fieldMappingAuditRows = ref<FieldMappingAuditTransition[]>([])
const expandedFieldPanels = ref<string[]>([])
const vehicleDataView = ref<VehicleDataView>('byVehicle')
const selectedVehicleBucket = ref<VehicleDataBucket | null>(null)
const vehicleDataSearch = ref('')
const vehicleDataPage = ref(1)
const vehicleDataRows = ref<PublishVehicleDataBucketVinRow[]>([])
const vehicleDataNextCursor = ref<string | undefined>(undefined)
const vehicleDataCursorStack = ref<Array<string | undefined>>([undefined])
const vehicleDataBucketStatus = ref<'idle' | 'loading' | 'ok' | 'error'>('idle')
const selectedVehicleDataVin = ref('')
const vehicleDataVinDetail = ref<PublishVehicleDataVinDetail | null>(null)
const vehicleDataVinDetailStatus = ref<'idle' | 'loading' | 'ok' | 'notFound' | 'error'>('idle')

const vehicleGroupBucket = ref<PublishVehicleGroupBucket>('changed')
const vehicleGroupSearch = ref('')
const vehicleGroupPage = ref(1)
const vehicleGroupRows = ref<PublishVehicleGroupBucketVinRow[]>([])
const vehicleGroupNextCursor = ref<string | undefined>()
const vehicleGroupCursorStack = ref<Array<string | undefined>>([undefined])
const vehicleGroupLoading = ref(false)
const vehicleGroupError = ref('')
const selectedVehicleGroupVin = ref('')
const vehicleGroupVinDetail = ref<PublishVehicleGroupVinDetail | null>(null)
const vehicleGroupVinDetailLoading = ref(false)
const vehicleGroupVinDetailError = ref('')

const listingVisibilityBucket = ref<PublishListingVisibilityBucket>('changed')
const listingVisibilitySearch = ref('')
const listingVisibilityPage = ref(1)
const listingVisibilityRows = ref<PublishListingVisibilityBucketVinRow[]>([])
const listingVisibilityNextCursor = ref<string | undefined>()
const listingVisibilityCursorStack = ref<Array<string | undefined>>([undefined])
const listingVisibilityLoading = ref(false)
const listingVisibilityError = ref('')
const listingVisibilityLoadedRequestKey = ref('')
const listingVisibilityDetailDialog = ref(false)
const selectedListingVisibilityVin = ref('')
const listingVisibilityVinDetail = ref<PublishListingVisibilityVinDetail | null>(null)
const listingVisibilityVinDetailLoading = ref(false)
const listingVisibilityVinDetailError = ref('')
const vinDrilldownDialog = ref(false)
const vinDrilldownStage = ref<VinDrilldownStage | null>(null)
// Which of the five website-impact categories the current websiteImpact-stage
// drilldown is showing. Drives the modal title, subtitle, and detail shape.
const websiteImpactDrilldownCategory = ref<WebsiteImpactCategory>('addedToWebsite')

const fieldMappingAuditSummary = computed(
  () => detailedImpactStore.summary?.auditSummaries?.fieldMapping ?? null,
)
const publishImpactViewModel = computed<PublishImpactPreviewViewModel>(() =>
  buildPublishImpactPreviewViewModel(detailedImpactStore.summary),
)

// ---------------------------------------------------------------------------
// Unified website-impact dataset. The Preview Summary grid, the Website
// Publish Outcome stage, and the View VINs modal ALL read from here, so their
// counts and VIN populations can never disagree. Categories mirror Publish
// Flow's five Run Summary metrics with predictive wording.
// ---------------------------------------------------------------------------
const WEBSITE_IMPACT_CATEGORY_ORDER: WebsiteImpactCategory[] = [
  'addedToWebsite',
  'removedFromWebsite',
  'updatedOnWebsite',
  'madeVisible',
  'hidden',
]

const websiteImpact = computed(() => publishImpactViewModel.value.websiteImpact)

type WebsiteImpactMetric = {
  id: WebsiteImpactCategory
  bucket: PublishListingVisibilityBucket
  label: string
  count: number
  detail: string
}

const websiteImpactMetrics = computed<WebsiteImpactMetric[]>(() =>
  WEBSITE_IMPACT_CATEGORY_ORDER.map((category) => ({
    id: category,
    bucket: category as PublishListingVisibilityBucket,
    label: websiteImpactCategoryLabel(category),
    count: websiteImpact.value[category] ?? 0,
    detail: websiteImpactCategoryDetail(category),
  })),
)

/**
 * Predictive hero line for the summary + outcome: how many vehicles will be
 * live on the website after publish. Sourced from the listing-visibility
 * outcome (final after-publish state), consistent with the unified dataset.
 */
const websiteLiveAfterPublishCount = computed<number | null>(() => {
  const outcome = detailedImpactStore.summary?.listingVisibilityOutcome
  if (!outcome || typeof outcome.visibleAfterPublish !== 'number') return null
  return outcome.visibleAfterPublish
})

const websiteLiveAfterPublishLabel = computed(() => {
  const live = websiteLiveAfterPublishCount.value
  if (live == null) return ''
  return `${live} ${pluralize(live, 'vehicle')} will be live on the website after publish.`
})

// Publish-Flow-style "Total" outcome cells, mapping the five unified
// websiteImpact categories onto Publish Flow's Added / Removed / Changed /
// Made Visible / Hidden labels. This is the SAME dataset as the metric cards
// — never a second count source.
type PreviewOutcomeScope = {
  addedToWebsite: number
  removedFromWebsite: number
  updatedOnWebsite: number
  madeVisible: number
  hidden: number
}

// Map the five unified website-impact categories onto Publish Flow's Added /
// Removed / Changed / Made Visible / Hidden labels. Shared by the Total,
// Feed-applied, and Manual cards so all three use identical structure.
function toPreviewOutcomeCells(scope: PreviewOutcomeScope) {
  return [
    { key: 'added', label: 'Added', value: scope.addedToWebsite },
    { key: 'removed', label: 'Removed', value: scope.removedFromWebsite },
    { key: 'changed', label: 'Changed', value: scope.updatedOnWebsite },
    { key: 'madeVisible', label: 'Made Visible', value: scope.madeVisible },
    { key: 'hidden', label: 'Hidden', value: scope.hidden },
  ]
}

const previewInventoryOutcomeTotalCells = computed(() =>
  toPreviewOutcomeCells(websiteImpact.value),
)

// Binary origin split cells. Only meaningful when `previewOutcomeSplitAvailable`
// is true (current artifacts). Legacy artifacts hide the Feed/Manual cards
// rather than presenting these zeros as authoritative.
const previewInventoryOutcomeFeedCells = computed(() =>
  toPreviewOutcomeCells(websiteImpact.value.feedApplied),
)

const previewInventoryOutcomeManualCells = computed(() =>
  toPreviewOutcomeCells(websiteImpact.value.manual),
)

const previewOutcomeSplitAvailable = computed(
  () => websiteImpact.value.splitAvailable,
)

// ---------------------------------------------------------------------------
// Preview "More info" metadata. Preview-only: every field is optional and only
// rendered when the value actually exists for this preview run. We never
// fabricate Publish-Flow-only values (triggered-by, correlation id, etc.).
// ---------------------------------------------------------------------------
const previewSimulationId = computed(
  () => simulationId.value ?? detailedImpactStore.summary?.runId ?? '',
)

const previewGeneratedAt = computed(
  () => detailedImpactStore.summary?.generatedAt || previewStore.result?.generatedAt || '',
)

const previewDurationLabel = computed(() => {
  const ms = previewStore.result?.guardrails?.durationMs
  if (typeof ms !== 'number' || ms < 0) return ''
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.round(ms / 100) / 10
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(ms / 60000)
  const remainderSeconds = Math.round((ms % 60000) / 1000)
  return `${minutes}m ${remainderSeconds}s`
})

const previewFeedSource = computed(() => {
  const diag = detailedImpactStore.summary?.stages?.preProcessor?.diagnostics
  if (!diag) return ''
  const source = diagnosticInput(diag)?.source
  return source ? formatFeedSource(source) : ''
})

const previewVinsEvaluatedLabel = computed(() => {
  const guardrails = previewStore.result?.guardrails
  if (!guardrails || typeof guardrails.evaluated !== 'number') return ''
  const total = typeof guardrails.totalVins === 'number' ? guardrails.totalVins : null
  return total != null && total !== guardrails.evaluated
    ? `${guardrails.evaluated} of ${total}`
    : `${guardrails.evaluated}`
})

const previewAccountId = computed(
  () => detailedImpactStore.summary?.jazelId || previewStore.result?.jazelId || '',
)

/**
 * Open the Publish-Flow-style five-category drilldown for one website-impact
 * category. Unlike the legacy visibility drilldown, this pins the modal to the
 * single clicked category (its unified `websiteImpact.<category>` VIN list) and
 * never renders the legacy Added/Removed/Reason/Unchanged tabs. The count and
 * VIN population therefore always match the clicked summary card.
 */
function openWebsiteImpactDrilldown(metric: WebsiteImpactMetric) {
  vinDrilldownStage.value = 'websiteImpact'
  vinDrilldownDialog.value = true
  websiteImpactDrilldownCategory.value = metric.id
  listingVisibilitySearch.value = ''
  selectedListingVisibilityVin.value = ''
  selectedVehicleDataVin.value = ''
  vehicleDataVinDetail.value = null
  vehicleDataVinDetailStatus.value = 'idle'
  listingVisibilityVinDetail.value = null
  listingVisibilityVinDetailError.value = ''
  // The category id doubles as the unified listing-visibility bucket name.
  listingVisibilityBucket.value = metric.bucket
  void loadListingVisibilityBucket({ reset: true })
}

async function selectWebsiteImpactVin(vin: string) {
  const trimmed = String(vin || '').trim()
  if (!trimmed) return

  if (websiteImpactDrilldownCategory.value === 'updatedOnWebsite') {
    // "Updated on website" is about data changes on a still-live listing, so
    // the detail shows field-level changes, NOT visibility-rule explanations.
    if (selectedListingVisibilityVin.value === trimmed && selectedVehicleDataVin.value === trimmed) return
    selectedListingVisibilityVin.value = trimmed
    selectedVehicleDataVin.value = trimmed
    await loadVehicleDataVinDetail(trimmed)
    return
  }

  // Added / removed / made visible / hidden are visibility transitions, so the
  // detail reuses the per-VIN visibility decision (before/after + reason).
  await selectListingVisibilityVin(trimmed)
}

const selectedWebsiteImpactRow = computed(() =>
  listingVisibilityRows.value.find((row) => row.vin === selectedListingVisibilityVin.value) ?? null,
)

function formatNumberRecord(
  counts: Record<string, number> | undefined,
  labeler: (key: string) => string = (key) => key,
): string {
  if (!counts || Object.keys(counts).length === 0) return ''

  return Object.entries(counts)
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key, count]) => `${count} ${labeler(key)}`)
    .join(' · ')
}

function humanizeAction(action: string): string {
  switch (action) {
    case 'set':
      return 'set'
    case 'replace':
      return 'replace'
    case 'append':
      return 'append'
    case 'remove':
      return 'remove'
    default:
      return action
  }
}

const feedFieldRulesStageSummary = computed(
  () => detailedImpactStore.summary?.stages?.feedFieldRules ?? null,
)

const feedFieldRulesImpact = computed(() => {
  const stage = feedFieldRulesStageSummary.value
  if (!stage || stage.availability !== 'available') return null

  const counts = stage.counts
  const diagnostics = stage.feedFieldRulesDiagnostics
  const evaluated = diagnostics?.evaluated ?? counts.evaluated

  if (evaluated <= 0) return null

  const changed = counts.changed
  const unchanged = counts.unchanged
  const skipped = counts.skipped
  const error = counts.error
  const affected = diagnostics?.affectedVehicles ?? changed
  const appliedRuleCount = diagnostics?.appliedRuleCount ?? 0

  return {
    evaluated,
    evaluatedLabel: `${evaluated} ${pluralize(evaluated, 'vehicle')} evaluated`,
    detail: [
      `${affected} ${pluralize(affected, 'vehicle')} affected by feed field rules`,
      appliedRuleCount > 0
        ? `${appliedRuleCount} ${pluralize(appliedRuleCount, 'rule transform')} applied`
        : '',
      unchanged > 0 ? `${unchanged} unchanged` : '',
      skipped > 0 ? `${skipped} skipped` : '',
      error > 0 ? `${error} could not be evaluated` : '',
    ].filter(Boolean).join(', ') + '.',
    fieldSummary: formatNumberRecord(diagnostics?.fieldsChanged, humanizeField),
    actionSummary: formatNumberRecord(diagnostics?.actionCounts, humanizeAction),
  }
})

type PublishObservabilitySummaryWithGroupImpact = PublishObservabilitySummary & {
  impact?: {
    byStage?: {
      groups?: {
        vinsChanged?: number
        vinsAdded?: number
        vinsRemoved?: number
        vinsUnchanged?: number
        vinsErrored?: number
        vinsSkipped?: number
        byGroup?: unknown[]
      }
    }
  }
}

const vehicleGroupsImpact = computed(() => {
  const summary = detailedImpactStore.summary as PublishObservabilitySummaryWithGroupImpact | null
  const stage = summary?.stages?.groups
  if (!stage || stage.availability !== 'available') return null

  const byStageGroups = summary?.impact?.byStage?.groups
  const counts = stage.counts

  const changed = byStageGroups?.vinsChanged ?? counts.changed
  const added = byStageGroups?.vinsAdded ?? 0
  const removed = byStageGroups?.vinsRemoved ?? 0
  const unchanged = byStageGroups?.vinsUnchanged ?? counts.unchanged
  const error = byStageGroups?.vinsErrored ?? counts.error
  const skipped = byStageGroups?.vinsSkipped ?? counts.skipped

  return {
    evaluated: counts.evaluated,
    changed,
    added,
    removed,
    unchanged,
    error,
    skipped,
    detail: [
      `${changed} ${pluralize(changed, 'vehicle')} with group changes`,
      added > 0 ? `${added} added to groups` : '',
      removed > 0 ? `${removed} removed from groups` : '',
      unchanged > 0 ? `${unchanged} unchanged` : '',
      error > 0 ? `${error} could not be evaluated` : '',
      skipped > 0 ? `${skipped} skipped` : '',
    ].filter(Boolean).join(', ') + '.',
    byGroup: byStageGroups?.byGroup ?? [],
  }
})

const hasVehicleGroupDrilldownChanges = computed(() => {
  const impact = vehicleGroupsImpact.value
  if (!impact) return false
  return impact.changed > 0 || impact.added > 0 || impact.removed > 0
})

const listingVisibilityImpact = computed(() => {
  const stage = detailedImpactStore.summary?.stages?.listingRules
  if (!stage || stage.availability !== 'available') return null

  const buckets = detailedImpactStore.summary?.listingVisibilityBuckets
  const outcome = detailedImpactStore.summary?.listingVisibilityOutcome
  const bucketDetailsAvailable = !!buckets
  const changed = buckets?.changed ?? stage.counts.changed
  const willAppear = buckets?.willAppear ?? 0
  const willHide = buckets?.willHide ?? 0
  const reasonChanged = buckets?.reasonChanged ?? 0
  const unchanged = buckets?.unchanged ?? stage.counts.unchanged
  const visibleAfterPublish = outcome ? outcome.visibleAfterPublish : undefined
  const hiddenAfterPublish = outcome ? outcome.hiddenAfterPublish : undefined
  const unknownAfterPublish = outcome?.unknownAfterPublish ?? 0
  const outcomeDetail = outcome
    ? [
        `${outcome.visibleAfterPublish} visible`,
        `${outcome.hiddenAfterPublish} hidden`,
        unknownAfterPublish > 0
          ? `${unknownAfterPublish} ${pluralize(unknownAfterPublish, 'vehicle')} could not determine visibility`
          : '',
      ].filter(Boolean).join(', ')
    : ''
  const deltaDetail = bucketDetailsAvailable
    ? [
        `${changed} changed`,
        `${unchanged} unchanged`,
        willAppear ? `${willAppear} made visible` : '',
        willHide ? `${willHide} hidden` : '',
        reasonChanged ? `${reasonChanged} reason changed` : '',
      ].filter(Boolean).join(', ')
    : `${changed} changed. Visibility buckets are not available for this preview`

  return {
    evaluated: stage.counts.evaluated,
    bucketDetailsAvailable,
    changed,
    willAppear,
    willHide,
    reasonChanged,
    unchanged,
    visibleAfterPublish,
    hiddenAfterPublish,
    unknownAfterPublish,
    detail: [deltaDetail, outcomeDetail ? `After publish: ${outcomeDetail}` : ''].filter(Boolean).join('. ') + '.',
  }
})

const manualOnlyPreview = computed(() =>
  Boolean(publishImpactViewModel.value.impactSummary.inventoryVisibility.manualOnly),
)

const vehicleGroupEmptyMessage = computed(() =>
  vehicleGroupSearch.value.trim()
    ? 'No vehicles match this VIN or group search.'
    : 'No vehicles found.',
)

const listingVisibilityEmptyMessage = computed(() =>
  listingVisibilitySearch.value.trim()
    ? 'No vehicles match this VIN or visibility reason search.'
    : 'No vehicles found.',
)

const listingVisibilitySelectedBucketAvailable = computed(() =>
  isVisibilityBucketAvailable(detailedImpactStore.summary, listingVisibilityBucket.value),
)

const fieldMappingFieldSummaryRows = computed(() =>
  fieldMappingSummaryRows(fieldMappingAuditSummary.value),
)
const fieldMappingDetailRowsByField = computed(() => {
  const rowsByField = new Map<string, FieldMappingAuditTransition[]>()

  for (const row of fieldMappingAuditRows.value) {
    const rows = rowsByField.get(row.field) ?? []
    rows.push(row)
    rowsByField.set(row.field, rows)
  }

  return rowsByField
})

const paginatedVehicleBucketRows = computed(() => vehicleDataRows.value)

const vehicleDataPageRangeLabel = computed(() => {
  if (vehicleDataBucketStatus.value === 'loading') return 'Loading...'
  if (vehicleDataRows.value.length === 0) return '0 vehicles'

  const page = vehicleDataPage.value
  const rows = vehicleDataRows.value.length
  const vehicleWord = pluralize(rows, 'vehicle')
  const bucket = selectedVehicleBucket.value
  const searchActive = vehicleDataSearch.value.trim().length > 0
  const totalInBucket =
    bucket && !searchActive ? vehicleDataBucketCount(bucket) : null

  if (totalInBucket != null && totalInBucket > 0) {
    const totalPages = Math.max(1, Math.ceil(totalInBucket / VEHICLE_DATA_PAGE_SIZE))
    return `Page ${page} of ${totalPages} · ${rows} ${vehicleWord}`
  }

  return `Page ${page} · ${rows} ${vehicleWord}`
})

const vehicleGroupRangeLabel = computed(() =>
  formatVinPageRangeLabel({
    loading: vehicleGroupLoading.value,
    page: vehicleGroupPage.value,
    shown: vehicleGroupRows.value.length,
    total: vehicleGroupSearch.value.trim()
      ? null
      : vehicleGroupBucketCount(vehicleGroupBucket.value),
    pageSize: VEHICLE_GROUP_PAGE_SIZE,
  }),
)

const listingVisibilityRangeLabel = computed(() =>
  formatVinPageRangeLabel({
    loading: listingVisibilityLoading.value,
    page: listingVisibilityPage.value,
    shown: listingVisibilityRows.value.length,
    total: listingVisibilitySearch.value.trim()
      ? null
      : listingVisibilityBucketCount(listingVisibilityBucket.value),
    pageSize: LISTING_VISIBILITY_PAGE_SIZE,
  }),
)

const vehicleDataEmptyMessage = computed(() =>
  vehicleDataSearch.value.trim()
    ? 'No vehicles match this VIN search.'
    : 'No vehicles found.',
)

const selectedVehicleDataVinDetails = computed(() =>
  (vehicleDataVinDetail.value?.fields ?? []).map(toVehicleDataVinFieldDetailRow),
)

const listingVisibilityVisibleCategories = computed<PublishListingVisibilityBucket[]>(() =>
  (['willAppear', 'willHide', 'reasonChanged', 'unchanged'] as PublishListingVisibilityBucket[])
    .filter((bucket) => listingVisibilityBucketCount(bucket) > 0),
)

const vinDrilldownTitle = computed(() => {
  if (vinDrilldownStage.value === 'vehicleData') {
    return selectedVehicleBucket.value
      ? `Vehicle Information: ${vehicleDataBucketTitle(selectedVehicleBucket.value)}`
      : 'Vehicle Information VINs'
  }
  if (vinDrilldownStage.value === 'groups') return 'Vehicle Groupings VINs'
  if (vinDrilldownStage.value === 'websiteImpact') {
    return websiteImpactCategoryLabel(websiteImpactDrilldownCategory.value)
  }
  if (vinDrilldownStage.value === 'visibility') return visibilityDrilldownTitle(listingVisibilityBucket.value)
  return 'VIN Drill-down'
})

const vinDrilldownSubtitle = computed(() => {
  if (vinDrilldownStage.value === 'vehicleData') {
    return 'Select a VIN to review the vehicle data changes in this report.'
  }
  if (vinDrilldownStage.value === 'groups') {
    return 'Select a VIN to review its SRP grouping changes.'
  }
  if (vinDrilldownStage.value === 'websiteImpact') {
    return websiteImpactDrilldownCategory.value === 'updatedOnWebsite'
      ? 'Select a VIN to review the website-affecting data changes for this still-live listing.'
      : websiteImpactCategoryDetail(websiteImpactDrilldownCategory.value) ||
          'Select a VIN to review this website change.'
  }
  if (vinDrilldownStage.value === 'visibility') {
    return visibilityDrilldownSubtitle(listingVisibilityBucket.value)
  }
  return 'Select a VIN to review the details used in this report.'
})

watch(
  () => props.active,
  (active) => {
    if (!active) return
    void retrySummary()
  },
  { immediate: true },
)

watch(
  () => ({
    active: props.active,
    simId: simulationId.value,
    summaryStatus: detailedImpactStore.summaryStatus,
  }),
  () => {
    fieldMappingAuditRows.value = []
    fieldMappingAuditStatus.value = 'idle'
    expandedFieldPanels.value = []
    vehicleDataView.value = 'byVehicle'
    selectedVehicleBucket.value = null
    vehicleDataSearch.value = ''
    vehicleDataPage.value = 1
    vehicleDataRows.value = []
    vehicleDataNextCursor.value = undefined
    vehicleDataCursorStack.value = [undefined]
    vehicleDataBucketStatus.value = 'idle'
    selectedVehicleDataVin.value = ''
    vehicleDataVinDetail.value = null
    vehicleDataVinDetailStatus.value = 'idle'
    vehicleGroupBucket.value = 'changed'
    vehicleGroupSearch.value = ''
    vehicleGroupPage.value = 1
    vehicleGroupRows.value = []
    vehicleGroupNextCursor.value = undefined
    vehicleGroupCursorStack.value = [undefined]
    vehicleGroupLoading.value = false
    vehicleGroupError.value = ''
    selectedVehicleGroupVin.value = ''
    vehicleGroupVinDetail.value = null
    vehicleGroupVinDetailLoading.value = false
    vehicleGroupVinDetailError.value = ''
    listingVisibilityBucket.value = 'changed'
    listingVisibilitySearch.value = ''
    listingVisibilityPage.value = 1
    listingVisibilityRows.value = []
    listingVisibilityNextCursor.value = undefined
    listingVisibilityCursorStack.value = [undefined]
    listingVisibilityLoading.value = false
    listingVisibilityError.value = ''
    listingVisibilityLoadedRequestKey.value = ''
    listingVisibilityDetailDialog.value = false
    selectedListingVisibilityVin.value = ''
    listingVisibilityVinDetail.value = null
    listingVisibilityVinDetailLoading.value = false
    listingVisibilityVinDetailError.value = ''
    vinLookupResultDialog.value = false
    vinDrilldownDialog.value = false
    vinDrilldownStage.value = null
    websiteImpactDrilldownCategory.value = 'addedToWebsite'
  },
  { immediate: true },
)

watch(
  () => vehicleDataSearch.value,
  () => {
    if (!vinDrilldownDialog.value || vinDrilldownStage.value !== 'vehicleData') return
    if (!selectedVehicleBucket.value) return
    void loadVehicleDataBucketPage({ reset: true })
  },
)

watch(
  () => vehicleGroupSearch.value,
  () => {
    if (!vinDrilldownDialog.value || vinDrilldownStage.value !== 'groups') return
    void loadVehicleGroupBucket({ reset: true })
  },
)

watch(
  () => listingVisibilitySearch.value,
  () => {
    if (!vinDrilldownDialog.value) return
    if (vinDrilldownStage.value !== 'visibility' && vinDrilldownStage.value !== 'websiteImpact') return
    void loadListingVisibilityBucket({ reset: true })
  },
)

watch(
  () => [
    vinDrilldownDialog.value,
    vinDrilldownStage.value,
    selectedVehicleBucket.value,
    fieldMappingAuditSummary.value,
  ],
  () => {
    if (!vinDrilldownDialog.value || vinDrilldownStage.value !== 'vehicleData') return
    const bucket = selectedVehicleBucket.value
    if (!bucket || !isVehicleDataBucketTabDisabled(bucket)) return
    const next = resolveFirstVehicleDataBucketWithData()
    if (next === bucket) return
    selectedVehicleBucket.value = next
    void loadVehicleDataBucketPage({ reset: true })
  },
)

async function retrySummary(force = false) {
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  if (!simId || !token || !base) return
  await detailedImpactStore.loadSummary({
    endpoint: base,
    simulationId: simId,
    sessionToken: token,
    force,
  })
}

const vinLookupInFlight = computed(() => detailedImpactStore.vinTrailStatus === 'loading')
const vinLookupDisabled = computed(() => vinLookupInFlight.value || vinInput.value.trim().length === 0)

async function submitVinLookup() {
  if (vinLookupInFlight.value) return
  vinLookupResultDialog.value = false
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  if (!simId || !token || !base) return
  await detailedImpactStore.lookupVin({
    endpoint: base,
    simulationId: simId,
    sessionToken: token,
    vin: vinInput.value,
  })
}

function closeVinLookupResultDialog() {
  vinLookupResultDialog.value = false
}

function handleVinLookupResultDialogUpdate(open: boolean) {
  if (!open) closeVinLookupResultDialog()
}

type FieldMappingFieldSummaryRow = {
  key: string
  field: string
  label: string
  affectedVehicles: number
  valueChanges: number
}

type FieldMappingAuditTransition = {
  key: string
  testId: string
  field: string
  label: string
  count: number
  beforeLabel: string
  afterLabel: string
  previousSource: string
  newSource: string
}

type VehicleDataBucketRow = PublishVehicleDataBucketVinRow

type VehicleDataVinFieldDetail = {
  key: string
  testId: string
  field: string
  label: string
  previousValue: string
  newValue: string
  previousSource: string
  newSource: string
  valueChanged: boolean
}

function setVehicleDataBucket(bucket: VehicleDataBucket) {
  const next = isVehicleDataBucketTabDisabled(bucket)
    ? resolveFirstVehicleDataBucketWithData()
    : bucket
  selectedVehicleBucket.value = next
  void loadVehicleDataBucketPage({ reset: true })
}

function openVehicleDataDrilldown(bucket: VehicleDataBucket) {
  vinDrilldownStage.value = 'vehicleData'
  vinDrilldownDialog.value = true
  setVehicleDataBucket(bucket)
}

function openVehicleGroupDrilldown(bucket: PublishVehicleGroupBucket) {
  vinDrilldownStage.value = 'groups'
  vinDrilldownDialog.value = true
  setVehicleGroupBucket(bucket)
}

function openListingVisibilityDrilldown(bucket: PublishListingVisibilityBucket) {
  vinDrilldownStage.value = 'visibility'
  vinDrilldownDialog.value = true
  setListingVisibilityBucket(bucket)
}

function closeVinDrilldownDialog() {
  vinDrilldownDialog.value = false
  vinDrilldownStage.value = null
}

function handleVinDrilldownDialogUpdate(open: boolean) {
  if (!open) closeVinDrilldownDialog()
}

function selectVehicleDataVin(vin: string) {
  if (selectedVehicleDataVin.value === vin) return
  selectedVehicleDataVin.value = vin
  void loadVehicleDataVinDetail(vin)
}

function goToVehicleDataPage(delta: number) {
  if (delta > 0) {
    if (!vehicleDataNextCursor.value) return
    void loadVehicleDataBucketPage({
      cursor: vehicleDataNextCursor.value,
      page: vehicleDataPage.value + 1,
    })
    return
  }

  if (delta < 0 && vehicleDataPage.value > 1) {
    const previousPage = vehicleDataPage.value - 1
    const previousCursor = vehicleDataCursorStack.value[previousPage - 1]
    void loadVehicleDataBucketPage({
      cursor: previousCursor,
      page: previousPage,
    })
  }
}

async function loadVehicleDataBucketPage(input: {
  reset?: boolean
  cursor?: string
  page?: number
} = {}) {
  const bucket = selectedVehicleBucket.value
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value

  if (!bucket || !simId || !token || !base) return

  if (input.reset) {
    vehicleDataPage.value = 1
    vehicleDataRows.value = []
    vehicleDataNextCursor.value = undefined
    vehicleDataCursorStack.value = [undefined]
    selectedVehicleDataVin.value = ''
    vehicleDataVinDetail.value = null
    vehicleDataVinDetailStatus.value = 'idle'
  }

  vehicleDataBucketStatus.value = 'loading'

  const requestedBucket = bucket
  const requestedSearch = vehicleDataSearch.value

  try {
    const result = await tryGetPublishPreviewVehicleDataBucket({
      endpoint: base,
      simulationId: simId,
      bucket,
      sessionToken: token,
      pageSize: VEHICLE_DATA_PAGE_SIZE,
      cursor: input.cursor,
      search: requestedSearch,
    })

    if (
      selectedVehicleBucket.value !== requestedBucket ||
      vehicleDataSearch.value !== requestedSearch
    ) {
      return
    }

    if (result.status !== 'ok') {
      vehicleDataRows.value = []
      vehicleDataNextCursor.value = undefined
      vehicleDataBucketStatus.value = 'error'
      return
    }

    const page = input.page ?? 1
    vehicleDataRows.value = result.data.records
    vehicleDataNextCursor.value = result.data.nextCursor
    vehicleDataPage.value = page

    const stack = [...vehicleDataCursorStack.value]
    stack[page - 1] = input.cursor
    if (result.data.nextCursor) {
      stack[page] = result.data.nextCursor
    }
    vehicleDataCursorStack.value = stack

    vehicleDataBucketStatus.value = 'ok'

    if (vehicleDataRows.value.length > 0) {
      selectVehicleDataVin(vehicleDataRows.value[0].vin)
    }
  } catch (error) {
    if (
      selectedVehicleBucket.value !== requestedBucket ||
      vehicleDataSearch.value !== requestedSearch
    ) {
      return
    }

    console.warn('Failed to load vehicle data bucket', error)
    vehicleDataRows.value = []
    vehicleDataNextCursor.value = undefined
    vehicleDataBucketStatus.value = 'error'
  }
}

async function loadVehicleDataVinDetail(vin: string) {
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  const trimmedVin = String(vin || '').trim()
  const requestedVin = trimmedVin

  if (!simId || !token || !base || !trimmedVin) return

  vehicleDataVinDetail.value = null
  vehicleDataVinDetailStatus.value = 'loading'

  try {
    const result = await tryGetPublishPreviewVehicleDataVinDetail({
      endpoint: base,
      simulationId: simId,
      vin: trimmedVin,
      sessionToken: token,
    })

    if (selectedVehicleDataVin.value !== requestedVin) return

    if (result.status === 'ok') {
      vehicleDataVinDetail.value = result.data
      vehicleDataVinDetailStatus.value = 'ok'
      return
    }

    if (result.status === 'notFound') {
      vehicleDataVinDetailStatus.value = 'notFound'
      return
    }

    vehicleDataVinDetailStatus.value = 'error'
  } catch (error) {
    if (selectedVehicleDataVin.value !== requestedVin) return

    console.warn('Failed to load vehicle data VIN detail', error)
    vehicleDataVinDetailStatus.value = 'error'
  }
}

function goToVehicleGroupPage(delta: number) {
  if (delta > 0) {
    if (!vehicleGroupNextCursor.value) return
    void loadVehicleGroupBucket({
      cursor: vehicleGroupNextCursor.value,
      page: vehicleGroupPage.value + 1,
    })
    return
  }

  if (delta < 0 && vehicleGroupPage.value > 1) {
    const previousPage = vehicleGroupPage.value - 1
    const previousCursor = vehicleGroupCursorStack.value[previousPage - 1]
    void loadVehicleGroupBucket({
      cursor: previousCursor,
      page: previousPage,
    })
  }
}

async function loadVehicleGroupBucket(input: {
  reset?: boolean
  cursor?: string
  page?: number
} = {}) {
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  if (!simId || !token || !base) return

  if (input.reset) {
    vehicleGroupPage.value = 1
    vehicleGroupRows.value = []
    vehicleGroupNextCursor.value = undefined
    vehicleGroupCursorStack.value = [undefined]
    selectedVehicleGroupVin.value = ''
    vehicleGroupVinDetail.value = null
  }

  const requestedBucket = vehicleGroupBucket.value
  const requestedSearch = vehicleGroupSearch.value

  vehicleGroupLoading.value = true
  vehicleGroupRows.value = []
  vehicleGroupNextCursor.value = undefined
  selectedVehicleGroupVin.value = ''
  vehicleGroupVinDetail.value = null
  vehicleGroupError.value = ''

  try {
    const result = await tryGetPublishPreviewVehicleGroupBucket({
      endpoint: base,
      simulationId: simId,
      sessionToken: token,
      bucket: requestedBucket,
      pageSize: VEHICLE_GROUP_PAGE_SIZE,
      cursor: input.cursor,
      search: requestedSearch,
    })

    if (
      vehicleGroupBucket.value !== requestedBucket ||
      vehicleGroupSearch.value !== requestedSearch
    ) {
      return
    }

    if (result.status !== 'ok') {
      vehicleGroupRows.value = []
      vehicleGroupNextCursor.value = undefined
      vehicleGroupError.value = friendlyVehicleGroupBucketError(requestedBucket)
      return
    }

    const page = input.page ?? 1
    vehicleGroupRows.value = result.data.records
    vehicleGroupNextCursor.value = result.data.nextCursor
    vehicleGroupPage.value = page

    const stack = [...vehicleGroupCursorStack.value]
    stack[page - 1] = input.cursor
    if (result.data.nextCursor) {
      stack[page] = result.data.nextCursor
    }
    vehicleGroupCursorStack.value = stack

    if (vehicleGroupRows.value.length > 0) {
      void selectVehicleGroupVin(vehicleGroupRows.value[0].vin)
    }
  } finally {
    vehicleGroupLoading.value = false
  }
}

async function selectVehicleGroupVin(vin: string) {
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  const trimmedVin = String(vin || '').trim()

  if (!simId || !token || !base || !trimmedVin) return
  if (selectedVehicleGroupVin.value === trimmedVin) return

  selectedVehicleGroupVin.value = trimmedVin
  vehicleGroupVinDetail.value = null
  vehicleGroupVinDetailError.value = ''
  vehicleGroupVinDetailLoading.value = true

  try {
    const result = await tryGetPublishPreviewVehicleGroupVinDetail({
      endpoint: base,
      simulationId: simId,
      vin: trimmedVin,
      sessionToken: token,
    })

    if (selectedVehicleGroupVin.value !== trimmedVin) return

    if (result.status !== 'ok') {
      vehicleGroupVinDetailError.value = result.message || 'Vehicle group detail is not available for this VIN.'
      return
    }

    vehicleGroupVinDetail.value = result.data
  } finally {
    if (selectedVehicleGroupVin.value === trimmedVin) {
      vehicleGroupVinDetailLoading.value = false
    }
  }
}

function setVehicleGroupBucket(bucket: PublishVehicleGroupBucket) {
  const next = isVehicleGroupBucketTabDisabled(bucket)
    ? resolveFirstVehicleGroupBucketWithData()
    : bucket
  if (vehicleGroupBucket.value === next) {
    void loadVehicleGroupBucket({ reset: true })
    return
  }
  vehicleGroupBucket.value = next
  void loadVehicleGroupBucket({ reset: true })
}

function searchVehicleGroups() {
  void loadVehicleGroupBucket({ reset: true })
}

function friendlyVehicleGroupBucketError(bucket: PublishVehicleGroupBucket): string {
  return `Detailed VINs for ${friendlyVehicleGroupBucketLabel(bucket)} were not captured for this preview. The summary count is available, but per-VIN drill-down is not yet available for this category.`
}

function friendlyVehicleGroupBucketLabel(bucket: PublishVehicleGroupBucket): string {
  switch (bucket) {
    case 'changed':
      return 'changed vehicle groupings'
    case 'added':
      return 'vehicles added to groupings'
    case 'removed':
      return 'vehicles removed from SRP groupings'
    case 'unchanged':
      return 'unchanged vehicle groupings'
    default:
      return 'this grouping category'
  }
}

function goToListingVisibilityPage(delta: number) {
  if (delta > 0) {
    if (!listingVisibilityNextCursor.value) return
    void loadListingVisibilityBucket({
      cursor: listingVisibilityNextCursor.value,
      page: listingVisibilityPage.value + 1,
    })
    return
  }

  if (delta < 0 && listingVisibilityPage.value > 1) {
    const previousPage = listingVisibilityPage.value - 1
    const previousCursor = listingVisibilityCursorStack.value[previousPage - 1]
    void loadListingVisibilityBucket({
      cursor: previousCursor,
      page: previousPage,
    })
  }
}

async function loadListingVisibilityBucket(input: {
  reset?: boolean
  cursor?: string
  page?: number
} = {}) {
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  if (!simId || !token || !base) return

  if (input.reset) {
    listingVisibilityPage.value = 1
    listingVisibilityRows.value = []
    listingVisibilityNextCursor.value = undefined
    listingVisibilityCursorStack.value = [undefined]
    selectedListingVisibilityVin.value = ''
    listingVisibilityVinDetail.value = null
    listingVisibilityDetailDialog.value = false
    listingVisibilityLoadedRequestKey.value = ''
  }

  const requestedBucket = listingVisibilityBucket.value
  const requestedSearch = listingVisibilitySearch.value
  if (!isVisibilityBucketAvailable(detailedImpactStore.summary, requestedBucket)) {
    listingVisibilityPage.value = 1
    listingVisibilityRows.value = []
    listingVisibilityNextCursor.value = undefined
    listingVisibilityCursorStack.value = [undefined]
    listingVisibilityLoadedRequestKey.value = listingVisibilityRequestKey({
      bucket: requestedBucket,
      search: requestedSearch,
      simId,
    })
    return
  }
  const requestedKey = listingVisibilityRequestKey({
    bucket: requestedBucket,
    search: requestedSearch,
    simId,
  })

  listingVisibilityLoading.value = true
  listingVisibilityRows.value = []
  listingVisibilityNextCursor.value = undefined
  selectedListingVisibilityVin.value = ''
  listingVisibilityVinDetail.value = null
  listingVisibilityDetailDialog.value = false
  listingVisibilityLoadedRequestKey.value = ''
  listingVisibilityError.value = ''

  try {
    const result = await tryGetPublishPreviewListingVisibilityBucket({
      endpoint: base,
      simulationId: simId,
      bucket: requestedBucket,
      pageSize: LISTING_VISIBILITY_PAGE_SIZE,
      cursor: input.cursor,
      search: requestedSearch,
      sessionToken: token,
    })

    if (
      listingVisibilityBucket.value !== requestedBucket ||
      listingVisibilitySearch.value !== requestedSearch
    ) {
      return
    }

    if (result.status !== 'ok') {
      if (result.status === 'notFound' && listingVisibilityBucketCount(requestedBucket) === 0) {
        listingVisibilityRows.value = []
        listingVisibilityNextCursor.value = undefined
        listingVisibilityLoadedRequestKey.value = requestedKey
        return
      }
      listingVisibilityError.value = result.message || 'Listing visibility details are not available yet.'
      return
    }

    const page = input.page ?? 1
    listingVisibilityRows.value = result.data.records
    listingVisibilityNextCursor.value = result.data.nextCursor
    listingVisibilityPage.value = page
    listingVisibilityLoadedRequestKey.value = requestedKey

    const stack = [...listingVisibilityCursorStack.value]
    stack[page - 1] = input.cursor
    if (result.data.nextCursor) {
      stack[page] = result.data.nextCursor
    }
    listingVisibilityCursorStack.value = stack

    if (listingVisibilityRows.value.length > 0) {
      if (vinDrilldownStage.value === 'websiteImpact') {
        void selectWebsiteImpactVin(listingVisibilityRows.value[0].vin)
      } else if (vinDrilldownStage.value === 'visibility') {
        void selectListingVisibilityVin(listingVisibilityRows.value[0].vin)
      }
    }
  } finally {
    listingVisibilityLoading.value = false
  }
}

function setListingVisibilityBucket(bucket: PublishListingVisibilityBucket) {
  const next = isListingVisibilityBucketTabDisabled(bucket)
    ? resolveFirstListingVisibilityBucketWithData()
    : bucket
  if (listingVisibilityBucket.value === next) {
    void loadListingVisibilityBucket({ reset: true })
    return
  }
  listingVisibilityBucket.value = next
  void loadListingVisibilityBucket({ reset: true })
}

function searchListingVisibility() {
  void loadListingVisibilityBucket({ reset: true })
}

watch(
  () => listingVisibilityDetailDialog.value,
  (open) => {
    if (open) return
    selectedListingVisibilityVin.value = ''
    listingVisibilityVinDetail.value = null
    listingVisibilityVinDetailError.value = ''
    listingVisibilityVinDetailLoading.value = false
  },
)

async function selectListingVisibilityVin(vin: string) {
  const simId = simulationId.value
  const token = accountSettings.sessionToken
  const base = endpoint.value
  const trimmedVin = String(vin || '').trim()

  if (!simId || !token || !base || !trimmedVin) return
  if (selectedListingVisibilityVin.value === trimmedVin) return

  selectedListingVisibilityVin.value = trimmedVin
  listingVisibilityDetailDialog.value = true
  listingVisibilityVinDetail.value = null
  listingVisibilityVinDetailError.value = ''
  listingVisibilityVinDetailLoading.value = true

  try {
    const result = await tryGetPublishPreviewListingVisibilityVinDetail({
      endpoint: base,
      simulationId: simId,
      vin: trimmedVin,
      sessionToken: token,
    })

    if (selectedListingVisibilityVin.value !== trimmedVin) return

    if (result.status !== 'ok') {
      listingVisibilityVinDetailError.value = result.message || 'Listing visibility detail is not available for this VIN.'
      return
    }

    listingVisibilityVinDetail.value = result.data
  } finally {
    if (selectedListingVisibilityVin.value === trimmedVin) {
      listingVisibilityVinDetailLoading.value = false
    }
  }
}

function closeListingVisibilityDetail() {
  listingVisibilityDetailDialog.value = false
  selectedListingVisibilityVin.value = ''
  listingVisibilityVinDetail.value = null
  listingVisibilityVinDetailError.value = ''
  listingVisibilityVinDetailLoading.value = false
}

function fieldMappingSummaryRows(
  summary: PublishFieldMappingAuditSummary | null,
): FieldMappingFieldSummaryRow[] {
  if (!summary) return []

  if (Array.isArray(summary.fieldSummaries) && summary.fieldSummaries.length > 0) {
    return summary.fieldSummaries
      .map((fieldSummary) => ({
        key: fieldSummary.field,
        field: fieldSummary.field,
        label: humanizeField(fieldSummary.field),
        affectedVehicles: fieldSummary.affectedVehicles,
        valueChanges: fieldSummary.valueChanges,
      }))
      .sort((a, b) =>
        b.affectedVehicles - a.affectedVehicles ||
        b.valueChanges - a.valueChanges ||
        a.label.localeCompare(b.label),
      )
  }

  // Backward-compatible fallback for older preview artifacts.
  // This is not authoritative because topFieldTransitions is capped.
  // New preview summaries should use summary.fieldSummaries instead.
  const byField = new Map<string, {
    field: string
    affectedVehicles: number
    valueChangeKeys: Set<string>
  }>()

  for (const transition of summary.topFieldTransitions ?? []) {
    const field = transition.field
    const entry = byField.get(field) ?? {
      field,
      affectedVehicles: 0,
      valueChangeKeys: new Set<string>(),
    }

    entry.affectedVehicles += transition.affectedVehicles
    entry.valueChangeKeys.add(`${transition.beforeValue}\u0000${transition.afterValue}`)
    byField.set(field, entry)
  }

  return Array.from(byField.values())
    .map((entry) => ({
      key: entry.field,
      field: entry.field,
      label: humanizeField(entry.field),
      affectedVehicles: entry.affectedVehicles,
      valueChanges: entry.valueChangeKeys.size,
    }))
    .sort((a, b) =>
      b.affectedVehicles - a.affectedVehicles ||
      b.valueChanges - a.valueChanges ||
      a.label.localeCompare(b.label),
    )
}

async function handleFieldMappingExpansion(value?: unknown) {
  const expandedValue = value ?? expandedFieldPanels.value
  const hasExpandedField = Array.isArray(expandedValue)
    ? expandedValue.length > 0
    : Boolean(expandedValue)

  if (!hasExpandedField) return
  if (fieldMappingAuditStatus.value !== 'idle') return

  const simId = simulationId.value
  if (!simId) return

  await loadFieldMappingAudit(simId)
}

async function loadFieldMappingAudit(simId: string) {
  if (fieldMappingAuditStatus.value === 'loading' || fieldMappingAuditStatus.value === 'ok') return

  const token = accountSettings.sessionToken
  const base = endpoint.value

  if (!token || !base) return

  fieldMappingAuditStatus.value = 'loading'

  const records: PublishDecisionRecord[] = []
  let cursor: string | undefined

  try {
    do {
      const result = await tryGetPublishPreviewStageDecisions({
        endpoint: base,
        simulationId: simId,
        stage: 'fieldMapping',
        sessionToken: token,
        pageSize: 500,
        cursor,
      })

      if (result.status !== 'ok') {
        fieldMappingAuditStatus.value = 'error'
        fieldMappingAuditRows.value = []
        return
      }

      records.push(...result.data.records)
      cursor = result.data.nextCursor
    } while (cursor)

    fieldMappingAuditRows.value = buildFieldMappingAuditRows(records)
    fieldMappingAuditStatus.value = 'ok'
  } catch (error) {
    console.warn('Failed to load field mapping audit details', error)
    fieldMappingAuditRows.value = []
    fieldMappingAuditStatus.value = 'error'
  }
}

function buildFieldMappingAuditRows(records: PublishDecisionRecord[]): FieldMappingAuditTransition[] {
  const transitions = new Map<string, {
    field: string
    beforeLabel: string
    afterLabel: string
    previousSource: string
    newSource: string
    vins: Set<string>
  }>()

  for (const record of records) {
    if (record.stage !== 'fieldMapping' || record.status !== 'changed') continue

    for (const reason of record.reasons ?? []) {
      const field = typeof reason.field === 'string' && reason.field.trim()
        ? reason.field.trim()
        : ''

      if (!field) continue

      // Detail rows are value/source rows.
      // Field-level summaries intentionally do not include source,
      // because source selection can vary per VIN due to fallback/default mapping logic.
      if (reason.changeKind && reason.changeKind !== 'value') continue

      const beforeLabel = formatAuditValue(reason.beforeValue)
      const afterLabel = formatAuditValue(reason.afterValue)

      if (beforeLabel === afterLabel) continue

      const previousSource = normalizeFeedSource(reason.sourceBefore)
      const newSource = normalizeFeedSource(reason.sourceAfter)

      const key = [
        field,
        beforeLabel,
        afterLabel,
        previousSource,
        newSource,
      ].join('\u0000')

      const entry = transitions.get(key) ?? {
        field,
        beforeLabel,
        afterLabel,
        previousSource,
        newSource,
        vins: new Set<string>(),
      }

      entry.vins.add(record.vin)
      transitions.set(key, entry)
    }
  }

  return Array.from(transitions.entries())
    .map(([key, entry]) => ({
      key,
      testId: [entry.field, entry.beforeLabel, entry.afterLabel, entry.previousSource, entry.newSource]
        .map(toSafeTestIdPart)
        .join('-'),
      field: entry.field,
      label: humanizeField(entry.field),
      beforeLabel: entry.beforeLabel,
      afterLabel: entry.afterLabel,
      previousSource: entry.previousSource,
      newSource: entry.newSource,
      count: entry.vins.size,
    }))
    .sort((a, b) =>
      b.count - a.count ||
      a.label.localeCompare(b.label) ||
      a.beforeLabel.localeCompare(b.beforeLabel) ||
      a.afterLabel.localeCompare(b.afterLabel),
    )
}

function toSafeTestIdPart(value: unknown): string {
  return String(value || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    || 'value'
}

function toVehicleDataVinFieldDetailRow(
  detail: PublishVehicleDataVinFieldDetail,
  index: number,
): VehicleDataVinFieldDetail {
  const previousValue = formatAuditValue(detail.previousValue)
  const newValue = formatAuditValue(detail.newValue)
  const previousSource = normalizeFeedSource(detail.previousSource)
  const newSource = normalizeFeedSource(detail.newSource)
  const keyParts = [
    detail.field,
    previousValue,
    newValue,
    previousSource,
    newSource,
    index,
  ]

  return {
    key: keyParts.join('\u0000'),
    testId: keyParts.map(toSafeTestIdPart).join('-'),
    field: detail.field,
    label: humanizeField(detail.field),
    previousValue,
    newValue,
    previousSource,
    newSource,
    valueChanged: detail.valueChanged,
  }
}

function normalizeFeedSource(source: unknown): string {
  if (typeof source !== 'string') return ''
  return source.trim()
}

const VEHICLE_DATA_BUCKET_ORDER: VehicleDataBucket[] = ['changed', 'unchanged', 'emptying']

function vehicleDataBucketCount(bucket: VehicleDataBucket): number | null {
  if (bucket === 'changed') {
    return fieldMappingAuditSummary.value?.vehicleDataBuckets?.changedVehicles
      ?? fieldMappingAuditSummary.value?.changedVehicles
      ?? null
  }

  if (bucket === 'unchanged') {
    return fieldMappingAuditSummary.value?.vehicleDataBuckets?.unchangedVehicles
      ?? fieldMappingAuditSummary.value?.unchangedVehicles
      ?? null
  }

  return fieldMappingAuditSummary.value?.vehicleDataBuckets?.vehiclesWithValuesBecomingEmpty
    ?? null
}

/** Disables modal bucket buttons when the audit summary reports exactly zero vehicles (unknown/null counts stay clickable). */
function isVehicleDataBucketTabDisabled(bucket: VehicleDataBucket): boolean {
  return vehicleDataBucketCount(bucket) === 0
}

function resolveFirstVehicleDataBucketWithData(): VehicleDataBucket {
  for (const b of VEHICLE_DATA_BUCKET_ORDER) {
    const n = vehicleDataBucketCount(b)
    if (n === null || n > 0) return b
  }
  return 'changed'
}

function vehicleDataBucketCountLabel(bucket: VehicleDataBucket): string {
  const count = vehicleDataBucketCount(bucket)
  if (count === null) return 'Load to calculate'
  return `${count} ${pluralize(count, 'vehicle')}`
}

function vehicleDataBucketTitle(bucket: VehicleDataBucket): string {
  switch (bucket) {
    case 'changed':
      return 'Changed vehicles'
    case 'unchanged':
      return 'Unchanged vehicles'
    case 'emptying':
      return 'Values becoming empty'
  }
}

function vehicleDataBucketDetail(bucket: VehicleDataBucket): string {
  const count = vehicleDataBucketCount(bucket)

  if (bucket === 'changed') {
    return `${count ?? 0} ${pluralize(count ?? 0, 'vehicle')} will have one or more data changes.`
  }

  if (bucket === 'unchanged') {
    return `${count ?? 0} ${pluralize(count ?? 0, 'vehicle')} have no data changes.`
  }

  if (count === null) {
    return 'Click to calculate which vehicles will have fields cleared.'
  }

  return `${count} ${pluralize(count, 'vehicle')} will have one or more fields cleared.`
}

function vehicleDataBucketListHelp(bucket: VehicleDataBucket): string {
  switch (bucket) {
    case 'changed':
      return 'Search or select a VIN to review its data changes.'
    case 'unchanged':
      return 'Search VINs with no vehicle-data changes.'
    case 'emptying':
      return 'Search VINs with one or more fields becoming empty.'
  }
}

function vehicleGroupBucketCount(bucket: PublishVehicleGroupBucket): number | null {
  const buckets = detailedImpactStore.summary?.vehicleGroupBuckets
  if (!buckets) return null
  return buckets[bucket] ?? 0
}

function isVehicleGroupBucketTabDisabled(bucket: PublishVehicleGroupBucket): boolean {
  return vehicleGroupBucketCount(bucket) === 0
}

function resolveFirstVehicleGroupBucketWithData(): PublishVehicleGroupBucket {
  for (const bucket of VEHICLE_GROUP_BUCKET_ORDER) {
    const count = vehicleGroupBucketCount(bucket)
    if (count === null || count > 0) return bucket
  }
  return 'changed'
}

function vehicleGroupBucketListHelp(bucket: PublishVehicleGroupBucket): string {
  switch (bucket) {
    case 'changed':
      return 'Search or select a VIN to review its SRP grouping changes.'
    case 'added':
      return 'Search VINs added to one or more SRP groupings.'
    case 'removed':
      return 'Search VINs removed from one or more SRP groupings.'
    case 'unchanged':
      return 'Search VINs with unchanged SRP grouping membership.'
  }
}

function formatVinPageRangeLabel(options: {
  loading: boolean
  page: number
  shown: number
  total: number | null
  pageSize: number
}): string {
  if (options.loading) return 'Loading...'
  if (options.shown === 0) return '0 vehicles'

  if (options.total !== null && options.total > 0) {
    const totalPages = Math.max(1, Math.ceil(options.total / options.pageSize))
    return `Page ${options.page} of ${totalPages} · ${options.shown} ${pluralize(options.shown, 'vehicle')}`
  }

  return `Page ${options.page} · ${options.shown} ${pluralize(options.shown, 'vehicle')}`
}

function changedFieldCountLabel(row: VehicleDataBucketRow): string {
  const count = row.changedFieldCount ?? 0
  if (count <= 0) return 'Change details unavailable'
  return `${count} ${pluralize(count, 'field')} affected`
}

function emptyingFieldsLabel(row: VehicleDataBucketRow): string {
  const fields = row.emptyingFields ?? []
  if (fields.length === 0) return 'No fields'
  return fields.map(humanizeField).join(', ')
}

function sourceSummaryForVinField(detail: VehicleDataVinFieldDetail): string {
  return sourceSummaryForValues(detail.previousSource, detail.newSource)
}

function sourceSummaryForTransition(transition: FieldMappingAuditTransition): string {
  return sourceSummaryForValues(transition.previousSource, transition.newSource)
}

function formatFeedSource(source: string): string {
  const raw = String(source || '').trim()
  const normalized = raw.toLowerCase().replace(/^import_/, '')
  if (normalized === 'vauto') return 'vAuto'
  if (normalized === 'homenet') return 'HomeNet'
  if (normalized === 'fdds1' || normalized === 'fdds2' || normalized === 'fdds') return 'FDDS'
  if (normalized === 'dealerslink') return 'DealersLink'
  if (normalized === 'chrome_cvd_1_0' || normalized === 'chrome_cds_v1' || normalized === 'chrome') return 'Chrome'
  if (normalized === 'manual_add' || normalized === 'custom_feed') return 'Manual add'
  return raw || 'Selected'
}

function formatSampleVins(values: string[]): string {
  if (!values || values.length === 0) return 'No sample VINs'
  return values.join(', ')
}

function sourceSummaryForValues(previous: string, next: string): string {
  const previousFormatted = previous ? formatFeedSource(previous) : ''
  const nextFormatted = next ? formatFeedSource(next) : ''

  if (!previousFormatted && !nextFormatted) return ''

  // Only one side carries a source: show it plainly.
  if (!previousFormatted || !nextFormatted) {
    return `Source: ${previousFormatted || nextFormatted}`
  }

  // Compare on the displayed value so sources that only differ by casing or an
  // `import_` prefix (e.g. `vauto` vs `vAuto`) resolve to the same feed and
  // never render a misleading "Source changed: X -> X".
  if (previousFormatted === nextFormatted) {
    return `Source: ${previousFormatted}`
  }

  return `Source changed: ${previousFormatted} -> ${nextFormatted}`
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural
}

function formatAuditValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'No value'

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed.toLowerCase() === 'empty') return 'No value'
    return trimmed
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  if (Array.isArray(value)) {
    return value.map(formatAuditValue).join(', ') || 'No value'
  }

  return '[complex value]'
}

function friendlyOperator(operator?: string): string {
  switch (operator) {
    case 'equals':
      return 'equals'
    case 'not_equals':
      return 'does not equal'
    case 'contains':
      return 'contains'
    case 'missing':
      return 'is empty'
    case 'not_missing':
      return 'is not empty'
    case 'length_not_in':
      return 'length is not'
    default:
      return operator ? humanizeField(operator) : 'matches'
  }
}

function formatPreviewValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'No value'
  if (Array.isArray(value)) return value.length ? value.map(formatPreviewValue).join(', ') : 'No value'
  if (typeof value === 'object') return '[complex value]'
  return String(value)
}

function humanizeField(value: string): string {
  return String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase()) || 'Field'
}

let lastSeenNonce = detailedImpactStore.pendingLookupNonce

watch(
  () => ({
    active: props.active,
    nonce: detailedImpactStore.pendingLookupNonce,
  }),
  ({ active, nonce }) => {
    const nonceChanged = nonce !== lastSeenNonce
    lastSeenNonce = nonce
    if (!active || !nonceChanged) return
    const pending = detailedImpactStore.consumePendingLookup()
    if (!pending) return
    vinInput.value = pending
    void submitVinLookup()
  },
  { immediate: true },
)

type AreaRow = {
  stage: PublishObservedStage | string
  label: string
  detail: string
  diagnostics?: PublishPreProcessorDiagnostics
  warningCount: number
  warningLabel: string
  warningTitle: string
}

const areaRows = computed<AreaRow[]>(() => {
  const summary = detailedImpactStore.summary
  if (!summary || !summary.stages) return []
  const rows: AreaRow[] = []
  // Group warnings by stage for the visible per-row indicator.
  const warningsByStage = new Map<string, string[]>()
  for (const w of summary.warnings ?? []) {
    if (!w?.stage) continue
    if (
      manualOnlyPreview.value &&
      w.stage === 'preProcessor' &&
      w.code === 'STAGE_NOT_EXECUTED'
    ) {
      continue
    }

    // Internal/supporting stages are displayed under their product-level rows.
    const warningStage =
      w.stage === 'feedFieldRules'
        ? 'fieldMapping'
        : w.stage

    const list = warningsByStage.get(warningStage) ?? []
    list.push(w.message)
    warningsByStage.set(warningStage, list)
  }
  for (const [stage, stageSummary] of Object.entries(summary.stages)) {
    if (!stageSummary) continue

    // Feed field rules are an internal sub-decision of Vehicle data.
    // They transform mapped feed values, so they should not appear as a
    // peer top-level publish-preview area.
    if (stage === 'feedFieldRules') continue

    rows.push(buildAreaRow(stage as PublishObservedStage, stageSummary, warningsByStage))
  }
  return rows.sort((a, b) => stageSortIndex(a.stage) - stageSortIndex(b.stage))
})

function stageSortIndex(stage: PublishObservedStage | string): number {
  // Mirror PUBLISH_FLOW_BUSINESS_SECTIONS order:
  // Feed Prep & Eligibility → Field Mapping → Inventory Sync → Vehicle
  // Groupings → Publish Rules Evaluation. `feedFieldRules` stays nested under
  // Field Mapping (it is filtered out of the top-level rows), and `feedIntake`
  // is never a Preview stage.
  const order = [
    'preProcessor',
    'feedFieldRules',
    'fieldMapping',
    'inventorySync',
    'groups',
    'listingRules',
  ]

  const index = order.indexOf(String(stage))
  return index === -1 ? order.length : index
}

const previewHeroSummary = computed(() => {
  const parts: string[] = []
  const flow = publishImpactViewModel.value.previewFlow

  if (flow.inventoryEligibility.continueCount > 0) {
    parts.push(`${flow.inventoryEligibility.continueCount} ${pluralize(flow.inventoryEligibility.continueCount, 'vehicle')} will continue after inventory eligibility`)
  }

  const visibility = flow.websiteVisibility
  if (visibility.visibleAfterPublish !== undefined && visibility.hiddenAfterPublish !== undefined) {
    parts.push(`${visibility.visibleAfterPublish} visible after publish`)
    parts.push(`${visibility.hiddenAfterPublish} hidden`)
  }

  return parts.length > 0
    ? `${parts.join(' · ')}.`
    : ''
})

const flowSteps = computed<PreviewFlowStep[]>(() =>
  buildPreviewFlowSteps(areaRows.value),
)

function buildPreviewFlowSteps(rows: AreaRow[]): PreviewFlowStep[] {
  const steps: Omit<PreviewFlowStep, 'stepNumber'>[] = []
  const cleanupRows = rows.filter((row) => isCleanupFlowStage(row.stage))
  const cleanupTarget = cleanupRows.find((row) => row.stage === 'preProcessor')
    ?? cleanupRows[0]

  if (cleanupTarget) {
    steps.push({
      id: 'inventoryEligibility',
      stage: cleanupTarget.stage,
      label: manualOnlyPreview.value ? 'Manual-only publish' : 'Feed Pre-processing',
      metric: cleanupFlowMetric(cleanupRows),
      detail: manualOnlyPreview.value
        ? 'Skips feed preprocessing because no primary sources are configured.'
        : 'Checks which vehicles are eligible to move forward.',
    })
  }

  for (const stage of ['fieldMapping', 'groups', 'listingRules']) {
    const row = rows.find((item) => item.stage === stage)
    if (!row) continue
    steps.push({
      id: String(row.stage),
      stage: row.stage,
      label: flowStepLabel(row.stage),
      metric: flowStepMetric(row),
      detail: flowStepDetail(row.stage),
    })
  }

  return steps.map((step, index) => ({
    ...step,
    stepNumber: index + 1,
  }))
}

function isCleanupFlowStage(stage: PublishObservedStage | string): boolean {
  return stage === 'preProcessor'
}

function cleanupFlowMetric(rows: AreaRow[]): string {
  if (manualOnlyPreview.value) {
    const hidden = publishImpactViewModel.value.impactSummary.inventoryVisibility.manualOnlyHiddenCount
    return hidden > 0
      ? `${hidden} feed-backed ${pluralize(hidden, 'vehicle')} removed`
      : 'Manual-only'
  }

  const preProcessorRow = rows.find((row) => row.stage === 'preProcessor')
  if (preProcessorRow?.diagnostics) {
    const output = diagnosticPreviewOutput(preProcessorRow.diagnostics)
    return `${output.audit.uniqueVinCount} continue`
  }

  return 'Checked'
}

function flowStepLabel(stage: PublishObservedStage | string): string {
  switch (stage) {
    case 'preProcessor':
      return 'Feed Pre-processing'
    case 'fieldMapping':
      return 'Field Mapping'
    case 'groups':
      return 'Vehicle Groupings'
    case 'listingRules':
      return 'Publish Rules Evaluation'
    default:
      return friendlyStageLabel(stage)
  }
}

function flowStepMetric(row: AreaRow): string {
  if (row.stage === 'preProcessor' && manualOnlyPreview.value) {
    return cleanupFlowMetric([row])
  }

  if (row.stage === 'preProcessor' && row.diagnostics) {
    const output = diagnosticPreviewOutput(row.diagnostics)
    return `${output.audit.uniqueVinCount} continue`
  }

  if (row.stage === 'fieldMapping') {
    const counts = stageCountsFor(row.stage)
    return counts ? `${counts.changed} changed` : 'Checked'
  }

  if (row.stage === 'listingRules' && listingVisibilityImpact.value) {
    const impact = listingVisibilityImpact.value
    if (impact.visibleAfterPublish !== undefined && impact.hiddenAfterPublish !== undefined) {
      return `${impact.visibleAfterPublish} visible · ${impact.hiddenAfterPublish} hidden`
    }
    return `${impact.changed} visibility changes`
  }

  if (row.stage === 'groups' && vehicleGroupsImpact.value) {
    return `${vehicleGroupsImpact.value.changed} grouping changes`
  }

  const counts = stageCountsFor(row.stage)
  if (counts) {
    return `${counts.changed} changed`
  }

  return 'Checked'
}

function flowStepDetail(stage: PublishObservedStage | string): string {
  switch (stage) {
    case 'preProcessor':
      if (manualOnlyPreview.value) {
        return 'Skips feed preprocessing because publish will run in manual-only mode.'
      }
      return 'Checks which vehicles are eligible to move forward.'
    case 'fieldMapping':
      return 'Updates vehicle details used by the website.'
    case 'listingRules':
      return 'Shows website visibility after publish.'
    case 'groups':
      return 'Updates website group membership.'
    default:
      return ''
  }
}

function stageCountsFor(stage: PublishObservedStage | string) {
  return detailedImpactStore.summary?.stages?.[stage as PublishObservedStage]?.counts
}

function scrollToPreviewStage(stage: PublishObservedStage | string) {
  const stageKey = String(stage)
  if (!expandedStagePanels.value.includes(stageKey)) {
    expandedStagePanels.value = [...expandedStagePanels.value, stageKey]
  }
  const element = document.getElementById(`preview-stage-${stage}`)
  element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function stagePlainDescription(stage: PublishObservedStage | string): string {
  switch (stage) {
    case 'preProcessor':
      if (manualOnlyPreview.value) {
        return 'This step is skipped because no primary sources are configured, so publish runs in manual-only mode.'
      }
      return 'This step checks which vehicles are eligible to move forward.'
    case 'fieldMapping':
      return 'This step shows vehicle information that will change, such as fuel type, condition, price, or selected data source.'
    case 'listingRules':
      return 'This step shows which vehicles will be visible or hidden on the website if you publish.'
    case 'groups':
      return 'This step shows which vehicle groupings vehicles will enter or leave.'
    default:
      return ''
  }
}

function stageInputOutput(row: AreaRow): StageInputOutput[] {
  if (row.stage === 'preProcessor' && manualOnlyPreview.value) {
    return []
  }

  if (row.stage === 'preProcessor' && row.diagnostics) {
    const inputAudit = diagnosticInputAudit(row.diagnostics)
    const input = diagnosticInput(row.diagnostics)
    const output = diagnosticPreviewOutput(row.diagnostics)

    return [
      {
        label: 'Input',
        value: `${inputAudit.rowCount} feed rows from ${formatFeedSource(input.source)}.`,
      },
      {
        label: 'Output',
        value: `${output.audit.uniqueVinCount} ${pluralize(output.audit.uniqueVinCount, 'vehicle')} continue to the next step.`,
      },
    ]
  }

  if (row.stage === 'fieldMapping') {
    const counts = stageCountsFor(row.stage)
    if (!counts) return []

    return [
      {
        label: 'Input',
        value: `${counts.evaluated} ${pluralize(counts.evaluated, 'vehicle')} checked for information updates.`,
      },
      {
        label: 'Output',
        value: `${counts.changed} ${pluralize(counts.changed, 'vehicle')} will have information changes.`,
      },
    ]
  }

  if (row.stage === 'listingRules' && listingVisibilityImpact.value) {
    const impact = listingVisibilityImpact.value
    const changeSummary = impact.changed > 0
      ? `${impact.changed} ${pluralize(impact.changed, 'vehicle')} will change website visibility.`
      : 'No vehicles will be made visible or hidden.'
    const finalStateSummary = impact.visibleAfterPublish !== undefined && impact.hiddenAfterPublish !== undefined
      ? ` After publish: ${impact.visibleAfterPublish} visible, ${impact.hiddenAfterPublish} hidden.`
      : ''

    return [
      {
        label: 'Input',
        value: `${impact.evaluated} ${pluralize(impact.evaluated, 'vehicle')} checked for website visibility.`,
      },
      {
        label: 'Output',
        value: `${changeSummary}${finalStateSummary}`,
      },
    ]
  }

  if (row.stage === 'groups' && vehicleGroupsImpact.value) {
    const impact = vehicleGroupsImpact.value

    return [
      {
        label: 'Input',
        value: `${impact.evaluated} ${pluralize(impact.evaluated, 'vehicle')} checked for vehicle groupings.`,
      },
      {
        label: 'Output',
        value: `${impact.changed} ${pluralize(impact.changed, 'vehicle')} will change groupings, ${impact.unchanged} unchanged.`,
      },
    ]
  }

  return []
}

function buildAreaRow(
  stage: PublishObservedStage,
  stageSummary: PublishStageSummary,
  warningsByStage: Map<string, string[]>,
): AreaRow {
  const label = stage === 'preProcessor' && manualOnlyPreview.value
    ? 'Manual-only publish'
    : flowStepLabel(stage)
  const availability: PublishStageAvailability = stageSummary.availability
  let detail: string
  if (availability === 'available') {
    if (
      stage === 'preProcessor' &&
      stageSummary.artifacts?.status === 'available' &&
      stageSummary.artifacts.recordCount === 0
    ) {
      detail = stageSummary.diagnostics?.explanation
        || 'Inventory pre-processing was evaluated; no inventory will be changed before mapping.'
    } else if (stage === 'preProcessor') {
      const counts = stageSummary.counts
      detail = `${counts.changed} will change${
        counts.skipped > 0 ? `, ${counts.skipped} will be removed/skipped before mapping` : ''
      }${counts.error > 0 ? `, ${counts.error} could not be evaluated` : ''}.`
    } else {
      const counts = stageSummary.counts
      detail = `${counts.changed} changed, ${counts.unchanged} unchanged${
        counts.skipped > 0 ? `, ${counts.skipped} skipped` : ''
      }${counts.error > 0 ? `, ${counts.error} could not be evaluated` : ''}.`
    }
  } else {
    if (stage === 'preProcessor' && manualOnlyPreview.value) {
      detail = 'Skipped because publish will run in manual-only mode with no primary sources configured.'
    } else {
      const friendly = friendlyAvailabilityCopy(availability, {
        reason: safeUnavailableReason(stage, availability, {
          unsupportedReason: stageSummary.unsupportedReason,
          deferredReason: stageSummary.deferredReason,
        }),
      })
      detail = friendly.detail ? `${friendly.label} ${friendly.detail}` : friendly.label
    }
  }
  const stageWarnings = warningsByStage.get(stage) ?? []
  const warningCount = stageWarnings.length
  return {
    stage,
    label,
    detail,
    ...(stage === 'preProcessor' && stageSummary.diagnostics && !manualOnlyPreview.value
      ? { diagnostics: stageSummary.diagnostics }
      : {}),
    warningCount,
    warningLabel: warningCount === 1 ? '1 warning' : `${warningCount} warnings`,
    warningTitle: stageWarnings.join(' • '),
  }
}

function preProcessorFilteredOutCount(diagnostics: PublishPreProcessorDiagnostics): number {
  const inputAudit = diagnosticInputAudit(diagnostics)
  const process = processAudit(diagnostics)
  return Math.max(0, inputAudit.uniqueVinCount - process.matchedUniqueVinCount)
}

function preProcessorOutcomeDetail(diagnostics: PublishPreProcessorDiagnostics): string {
  const filteredOut = preProcessorFilteredOutCount(diagnostics)
  const inputIssueCount = diagnosticInputAudit(diagnostics).duplicateVinRowCount
    + diagnosticInputAudit(diagnostics).invalidVinRowCount
  const parts = [
    filteredOut > 0
      ? `${filteredOut} ${pluralize(filteredOut, 'vehicle')} excluded by unpublished filters`
      : 'No vehicles excluded by unpublished filters',
    inputIssueCount > 0
      ? `${inputIssueCount} feed ${pluralize(inputIssueCount, 'row')} excluded for duplicate or invalid VINs`
      : 'no duplicate or invalid VIN rows',
  ]

  return `${parts.join('; ')}.`
}

function diagnosticInputVinIssueLabel(diagnostics: PublishPreProcessorDiagnostics): string {
  const audit = diagnosticInputAudit(diagnostics)
  if (audit.duplicateVinRowCount === 0 && audit.invalidVinRowCount === 0) return 'None'
  return [
    audit.duplicateVinRowCount > 0
      ? `${audit.duplicateVinRowCount} duplicate ${pluralize(audit.duplicateVinRowCount, 'row')}`
      : '',
    audit.invalidVinRowCount > 0
      ? `${audit.invalidVinRowCount} invalid ${pluralize(audit.invalidVinRowCount, 'row')}`
      : '',
  ].filter(Boolean).join(', ')
}

function processVinIssueLabel(diagnostics: PublishPreProcessorDiagnostics): string {
  const audit = processAudit(diagnostics)
  if (audit.matchedDuplicateVinRowCount === 0 && audit.matchedInvalidVinRowCount === 0) return 'None'
  return [
    audit.matchedDuplicateVinRowCount > 0
      ? `${audit.matchedDuplicateVinRowCount} duplicate ${pluralize(audit.matchedDuplicateVinRowCount, 'row')}`
      : '',
    audit.matchedInvalidVinRowCount > 0
      ? `${audit.matchedInvalidVinRowCount} invalid ${pluralize(audit.matchedInvalidVinRowCount, 'row')}`
      : '',
  ].filter(Boolean).join(', ')
}

function outputVinIssueLabel(diagnostics: PublishPreProcessorDiagnostics): string {
  const audit = diagnosticPreviewOutput(diagnostics).audit
  if (audit.duplicateVinRowCount === 0 && audit.invalidVinRowCount === 0) return 'None'
  return [
    audit.duplicateVinRowCount > 0
      ? `${audit.duplicateVinRowCount} duplicate ${pluralize(audit.duplicateVinRowCount, 'row')}`
      : '',
    audit.invalidVinRowCount > 0
      ? `${audit.invalidVinRowCount} invalid ${pluralize(audit.invalidVinRowCount, 'row')}`
      : '',
  ].filter(Boolean).join(', ')
}

function formatFieldValues(values: PublishPreProcessorFieldValueCount[], otherCount: number): string {
  const parts = values
    .map((value) => `${value.value}: ${value.count}`)
  if (otherCount > 0) parts.push(`Other: ${otherCount}`)
  return parts.length > 0 ? parts.join(', ') : 'none found'
}

function formatFeedFileName(fileName: string | undefined): string {
  const trimmed = String(fileName || '').trim()
  return trimmed || 'latest feed file'
}

function diagnosticInput(diagnostics: PublishPreProcessorDiagnostics) {
  return diagnostics.input?.feed ?? diagnostics.feed
}

function diagnosticInputFiles(
  diagnostics: PublishPreProcessorDiagnostics,
): PublishPreProcessorFeedFileReference[] {
  const files = diagnostics.input?.feed.files
  if (Array.isArray(files) && files.length > 0) {
    return files.filter((file) => !isPreProcessorOutputFile(file))
  }

  const file = diagnostics.input?.feed.file ?? diagnostics.feed.file
  return file && !isPreProcessorOutputFile(file) ? [file] : []
}

function diagnosticOutputFile(
  diagnostics: PublishPreProcessorDiagnostics,
): PublishPreProcessorFeedFileReference | undefined {
  return diagnostics.output?.feed.file
    ?? diagnostics.previewOutput?.feed.file
    ?? (
      diagnostics.feed.file && isPreProcessorOutputFile(diagnostics.feed.file)
        ? diagnostics.feed.file
        : undefined
    )
}

function diagnosticInputAudit(diagnostics: PublishPreProcessorDiagnostics) {
  return diagnostics.input?.audit ?? {
    rowCount: diagnosticInput(diagnostics).vehicleCount,
    uniqueVinCount: diagnosticInput(diagnostics).vehicleCount,
    duplicateVinRowCount: 0,
    invalidVinRowCount: 0,
  }
}

function diagnosticPreviewOutput(diagnostics: PublishPreProcessorDiagnostics) {
  if (diagnostics.previewOutput) return diagnostics.previewOutput
  const audit = processAudit(diagnostics)
  return {
    feed: {
      source: diagnostics.feed.source,
      vehicleCount: audit.matchedUniqueVinCount,
    },
    audit: {
      rowCount: audit.matchedRowCount,
      uniqueVinCount: audit.matchedUniqueVinCount,
      duplicateVinRowCount: audit.matchedDuplicateVinRowCount,
      invalidVinRowCount: audit.matchedInvalidVinRowCount,
    },
  }
}

function processAudit(diagnostics: PublishPreProcessorDiagnostics) {
  return {
    matchedRowCount: diagnostics.process?.matchedRowCount ?? diagnostics.totalUniqueMatches,
    matchedUniqueVinCount: diagnostics.process?.matchedUniqueVinCount ?? diagnostics.totalUniqueMatches,
    matchedDuplicateVinRowCount: diagnostics.process?.matchedDuplicateVinRowCount ?? 0,
    matchedInvalidVinRowCount: diagnostics.process?.matchedInvalidVinRowCount ?? 0,
  }
}

function isPreProcessorOutputFile(file: PublishPreProcessorFeedFileReference) {
  return /_(shared_inventory|merged)_feed\./i.test(file.fileName || file.key)
}

function jazelIdFromFeedKey(key: string): string {
  const firstPathSegment = String(key || '').split('/')[0]
  const currentJazelId = detailedImpactStore.manifest?.jazelId || accountSettings.accountId
  return firstPathSegment === 'raw' ? currentJazelId : firstPathSegment || currentJazelId
}

async function openFeedFile(
  file: PublishPreProcessorFeedFileReference | undefined,
  fileRole: 'sourceInput' | 'preProcessorOutput' = 'sourceInput',
) {
  const key = String(file?.key || '').trim()
  const base = endpoint.value
  const token = accountSettings.sessionToken
  if (!file || !key || !base || !token) return

  openingFeedKey.value = key
  try {
    const response = await getImportFeedFilePresign({
      endpoint: base,
      jazelId: jazelIdFromFeedKey(key),
      key,
      bucket: file.bucket,
      fileRole,
      simulationId: simulationId.value ?? undefined,
      sessionToken: token,
      expiresIn: 900,
    })
    if (!response.presignedUrl) {
      throw new Error('Unable to generate feed file link.')
    }
    window.open(response.presignedUrl, '_blank', 'noopener')
  } catch (error) {
    console.error('Failed to open pre-processor feed file', error)
    window.alert(error instanceof Error ? error.message : 'Unable to open feed file.')
  } finally {
    openingFeedKey.value = ''
  }
}

type VinRow = {
  stage: PublishObservedStage | string
  label: string
  statusLabel: string
  detail?: string
  changes: Array<{
    key: string
    label: string
    before: string
    after: string
  }>
}

const vinTrailRows = computed<VinRow[]>(() => {
  const trail = detailedImpactStore.vinTrail
  if (trail?.pipelineTrail) {
    return [
      {
        stage: 'source',
        label: 'Source',
        statusLabel: normalizePreviewCopy(trail.pipelineTrail.source.summary),
        changes: [],
      },
      ...trail.pipelineTrail.stages
        .filter((stage) => shouldShowPipelineStage(stage))
        .map((stage) => ({
          stage: stage.key,
          label: stage.label,
          statusLabel: pipelineOutcomeLabel(stage),
          detail: pipelineStageDetail(stage),
          changes: pipelineChangeRows(stage),
        })),
    ]
  }
  if (!trail || !trail.stages) return []
  const rows: VinRow[] = []
  for (const [stage, entry] of Object.entries(trail.stages)) {
    if (!entry) continue
    const label = friendlyStageLabel(stage as PublishObservedStage)
    const availability = entry.availability
    if (availability === 'available') {
      if (entry.record) {
        rows.push({
          stage,
          label,
          statusLabel: friendlyDecisionStatus(entry.record.status),
          detail: normalizePreviewCopy(friendlyVinRecordDetail(stage, entry.record)),
          changes: [],
        })
      } else {
        // Distinct messaging vs. unsupported/deferred: this area was
        // simulated AND this VIN was unaffected.
        const empty = friendlyAvailabilityCopy(availability, { hasRecord: false })
        rows.push({ stage, label, statusLabel: empty.label, changes: [] })
      }
    } else {
      const friendly = friendlyAvailabilityCopy(availability, {
        reason: safeUnavailableReason(stage, availability, {
          unsupportedReason: entry.reason,
          deferredReason: entry.reason,
        }),
      })
      rows.push({
        stage,
        label,
        statusLabel: friendly.label,
        detail: normalizePreviewCopy(friendly.detail),
        changes: [],
      })
    }
  }
  return rows
})

watch(
  () => [detailedImpactStore.vinTrailReady, vinTrailRows.value.length],
  () => {
    if (!detailedImpactStore.vinTrailReady || vinTrailRows.value.length === 0) return
    vinLookupResultDialog.value = true
  },
  { immediate: true },
)

function vinTrailStatusColor(row: VinRow): 'primary' | 'success' | 'warning' | 'error' | undefined {
  const status = row.statusLabel.toLowerCase()
  if (status.includes('hidden') || status.includes('removed') || status.includes('blocked')) return 'warning'
  if (status.includes('change') || status.includes('show')) return 'primary'
  if (status.includes('passed') || status.includes('kept')) return 'success'
  if (status.includes('error')) return 'error'
  return undefined
}

function pipelineOutcomeLabel(stage: PublishVinPipelineTrailStage): string {
  switch (stage.outcome) {
    case 'passed':
      return 'Passed'
    case 'blocked':
      return 'Blocked'
    case 'skipped':
      return 'Skipped'
    case 'kept':
      return 'Kept'
    case 'removed':
      return 'Removed'
    case 'changed':
      return 'Will change'
    case 'unchanged':
      return 'No change'
    case 'shown':
      return 'Will show'
    case 'hidden':
      return 'Will be hidden'
    case 'unsupported':
      return 'Not supported yet'
    case 'unknown':
    default:
      return 'Unknown'
  }
}

function shouldShowPipelineStage(_stage: PublishVinPipelineTrailStage): boolean {
  return true
}

function pipelineStageDetail(stage: PublishVinPipelineTrailStage): string | undefined {
  const reasonDetail = pipelineReasonDetail(stage)
  const changesSummary = shouldShowPipelineChangesAsRows(stage) ? '' : pipelineChangesSummary(stage)
  const details = Array.from(new Set([reasonDetail, cleanPipelineDetail(stage.detail), changesSummary]
    .map((value) => String(value || '').trim())
    .filter(Boolean)))

  if (details.length > 0) return normalizePreviewCopy(details.join(' '))
  const summary = cleanPipelineDetail(stage.summary)
  return summary && !isSummaryRepeatingOutcome(stage, summary) ? normalizePreviewCopy(summary) : undefined
}

function cleanPipelineDetail(value?: string): string {
  const text = String(value || '').trim()
  if (!text) return ''
  const codeMatch = text.match(/^Reason:\s*([A-Z0-9_ -]+)\.?$/i)
  if (codeMatch) return friendlyPipelineReason(codeMatch[1])
  return normalizePreviewCopy(text)
}

function normalizePreviewCopy(value?: string): string {
  return String(value || '').replace(/\bCurrentInventory\b/g, 'Inventory')
}

function pipelineReasonDetail(stage: PublishVinPipelineTrailStage): string {
  if (!stage.reasonCode) return ''
  return friendlyPipelineReason(stage.reasonCode)
}

function friendlyPipelineReason(reason: string): string {
  const normalized = reason.trim().toUpperCase().replace(/[\s-]+/g, '_')
  switch (normalized) {
    case 'REQUIRED_FIELD_MISSING':
      return 'Hidden because required vehicle information is missing.'
    case 'VIN_INVALID_OR_MISSING':
      return 'Hidden because the VIN is missing or invalid.'
    case 'VIN_MANUALLY_BLOCKED':
      return 'Hidden because this VIN was manually hidden.'
    default:
      return humanizeField(reason)
  }
}

function isSummaryRepeatingOutcome(stage: PublishVinPipelineTrailStage, summary: string): boolean {
  const normalizedSummary = summary.trim().toLowerCase().replace(/[.!]+$/g, '')
  const normalizedOutcome = pipelineOutcomeLabel(stage).trim().toLowerCase()
  return normalizedSummary === normalizedOutcome
    || normalizedSummary.startsWith(`${normalizedOutcome} `)
}

function pipelineChangesSummary(stage: PublishVinPipelineTrailStage): string {
  const changes = stage.changes ?? []
  if (changes.length === 0) return ''
  return changes
    .slice(0, 5)
    .map((change) => {
      const label = humanizeField(change.label)
      const hasBefore = Object.prototype.hasOwnProperty.call(change, 'before')
      const hasAfter = Object.prototype.hasOwnProperty.call(change, 'after')
      if (hasBefore || hasAfter) {
        return `${label}: ${formatPreviewValue(change.before)} -> ${formatPreviewValue(change.after)}.`
      }
      return `${label}.`
    })
    .join(' ')
}

function shouldShowPipelineChangesAsRows(stage: PublishVinPipelineTrailStage): boolean {
  return (stage.key === 'fieldMapping' || stage.key === 'groups') && (stage.changes?.length ?? 0) > 0
}

function pipelineChangeRows(stage: PublishVinPipelineTrailStage): VinRow['changes'] {
  if (!shouldShowPipelineChangesAsRows(stage)) return []
  return (stage.changes ?? []).slice(0, 8).map((change, index) => ({
    key: `${stage.key}-${change.label}-${index}`,
    label: pipelineChangeLabel(stage, change),
    before: formatPipelineChangeValue(stage, change.before, 'before'),
    after: formatPipelineChangeValue(stage, change.after, 'after'),
  }))
}

function pipelineChangeLabel(
  stage: PublishVinPipelineTrailStage,
  change: PublishVinPipelineTrailChange,
): string {
  const label = humanizeField(change.label).replace(/\.$/, '')
  if (stage.key !== 'groups') return label
  if (/removed/i.test(label)) return 'Removed from'
  if (/added/i.test(label)) return 'Added to'
  return label || 'Vehicle group'
}

function formatPipelineChangeValue(
  stage: PublishVinPipelineTrailStage,
  value: unknown,
  side: 'before' | 'after',
): string {
  const formatted = normalizePreviewCopy(formatPreviewValue(value))
  if (stage.key === 'groups') {
    const groupNames = formatGroupNamesFromValue(value)
    if (groupNames) return groupNames
    if (formatted === '[complex value]') return side === 'before' ? 'Assigned group' : 'Changed group'
    if (formatted === 'No value') return side === 'after' ? 'Removed' : 'None'
  }
  return formatted
}

function formatGroupNamesFromValue(value: unknown): string {
  const groups = Array.isArray(value) ? value : [value]
  const names = groups
    .map((group) => {
      if (!group || typeof group !== 'object' || Array.isArray(group)) return ''
      const record = group as Record<string, unknown>
      return String(record.groupName || record.groupId || '').trim()
    })
    .filter(Boolean)

  return names.join(', ')
}

function isRecordObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function friendlyGroupsPreviewDetail(record: PublishDecisionRecord): string {
  if (record.status === 'error') {
    return 'Vehicle groups could not be evaluated for this vehicle.'
  }
  if (record.status === 'skipped') {
    return 'Vehicle groups were skipped for this vehicle.'
  }

  const after = isRecordObject(record.after) ? record.after : {}
  const added = Array.isArray(after.added) ? after.added : []
  const removed = Array.isArray(after.removed) ? after.removed : []

  const parts = [
    added.length > 0 ? `${added.length} ${pluralize(added.length, 'group')} added` : '',
    removed.length > 0 ? `${removed.length} ${pluralize(removed.length, 'group')} removed` : '',
  ].filter(Boolean)

  return parts.length > 0
    ? `${parts.join(', ')}.`
    : 'Vehicle group membership was evaluated for this vehicle.'
}

function visibilityDecisionLabel(value?: 'listed' | 'delisted'): string {
  if (value === 'listed') return 'Visible online'
  if (value === 'delisted') return 'Hidden'
  return 'Unknown'
}

function listingVisibilityRowSubtitle(row: PublishListingVisibilityBucketVinRow): string {
  if (listingVisibilityVisibleCategories.value.length <= 1) return ''
  return `${visibilityDecisionLabel(row.beforeDecision)} -> ${visibilityDecisionLabel(row.afterDecision)}`
}

function websiteImpactRowSubtitle(row: PublishListingVisibilityBucketVinRow): string {
  return `${visibilityDecisionLabel(row.beforeDecision)} -> ${visibilityDecisionLabel(row.afterDecision)}`
}

function listingMatchedRuleSummary(rule?: PublishListingVisibilityMatchedRuleDetail): string {
  if (!rule) return 'No rule detail was captured.'
  if (rule.field) {
    return `${humanizeField(rule.field)} ${friendlyOperator(rule.operator)} ${formatPreviewValue(rule.expectedValue)}. Current value: ${formatPreviewValue(rule.actualValue)}.`
  }
  return rule.ruleId ? `Matched rule: ${rule.ruleId}.` : 'Matched visibility rule.'
}

function listingVisibilityRuleCardTitle(detail: PublishListingVisibilityVinDetail): string {
  return detail.beforeDecision !== detail.afterDecision ? 'Why this changed' : 'Matched rule'
}

function listingVisibilityBucketCount(bucket: PublishListingVisibilityBucket): number {
  return visibilityBucketCount(detailedImpactStore.summary, bucket)
}

function isListingVisibilityBucketTabDisabled(bucket: PublishListingVisibilityBucket): boolean {
  return listingVisibilityBucketCount(bucket) === 0
}

function resolveFirstListingVisibilityBucketWithData(): PublishListingVisibilityBucket {
  for (const bucket of LISTING_VISIBILITY_BUCKET_ORDER) {
    if (listingVisibilityBucketCount(bucket) > 0) return bucket
  }
  return 'willAppear'
}

function visibilityCategoryLabel(bucket: PublishListingVisibilityBucket): string {
  const count = listingVisibilityBucketCount(bucket)
  switch (bucket) {
    case 'willAppear':
      return `Added online (${count})`
    case 'willHide':
      return `Removed online (${count})`
    case 'reasonChanged':
      return `Reason changed (${count})`
    case 'unchanged':
      return `Unchanged (${count})`
    default:
      return `Vehicles (${count})`
  }
}

function visibilityDrilldownTitle(bucket: PublishListingVisibilityBucket): string {
  switch (bucket) {
    case 'willAppear':
      return 'Added Online Vehicles'
    case 'willHide':
      return 'Removed Online Vehicles'
    case 'reasonChanged':
      return 'Visibility Reason Changes'
    case 'unchanged':
      return 'Unchanged Website Visibility'
    case 'addedToWebsite':
    case 'removedFromWebsite':
    case 'updatedOnWebsite':
    case 'madeVisible':
    case 'hidden':
      return websiteImpactCategoryLabel(bucket)
    default:
      return 'Website Visibility VINs'
  }
}

function visibilityDrilldownSubtitle(bucket: PublishListingVisibilityBucket): string {
  switch (bucket) {
    case 'willAppear':
      return 'Select a VIN to see why it will be added online after publish.'
    case 'willHide':
      return 'Select a VIN to see why it will be removed from the website.'
    case 'reasonChanged':
      return 'Select a VIN to review the visibility reason change.'
    case 'unchanged':
      return 'Select a VIN to review its unchanged website visibility.'
    case 'addedToWebsite':
    case 'removedFromWebsite':
    case 'updatedOnWebsite':
    case 'madeVisible':
    case 'hidden':
      return websiteImpactCategoryDetail(bucket) || 'Select a VIN to review this website change.'
    default:
      return 'Select a VIN to review its website visibility.'
  }
}

function listingVisibilityRequestKey(options: {
  bucket?: PublishListingVisibilityBucket
  search?: string
  simId?: string
} = {}): string {
  return [
    options.simId ?? simulationId.value ?? '',
    options.bucket ?? listingVisibilityBucket.value,
    (options.search ?? listingVisibilitySearch.value).trim(),
  ].join('|')
}

function friendlyVinRecordDetail(stage: string, record: PublishDecisionRecord) {
  if (stage === 'preProcessor') return friendlyPreProcessorPreviewDetail(record)
  if (stage === 'listingRules') return friendlyListingRulesPreviewDetail(record)
  if (stage === 'feedFieldRules') return friendlyFeedFieldRulesPreviewDetail(record)
  if (stage === 'groups') return friendlyGroupsPreviewDetail(record)
  if (stage === 'inventorySync') return friendlyInventorySyncPreviewDetail(record)
  return friendlyAvailabilityCopy('available', { hasRecord: true }).label
}

function safeUnavailableReason(
  stage: PublishObservedStage | string,
  availability: PublishStageAvailability,
  reasons: { unsupportedReason?: string; deferredReason?: string },
): string | undefined {
  if (stage === 'preProcessor') {
    return 'Inventory pre-processing was not evaluated for this preview run.'
  }
  if (availability === 'unsupported') return reasons.unsupportedReason
  if (availability === 'deferred') return reasons.deferredReason
  return undefined
}

</script>

<style scoped>
.publish-change-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.preview-website-outcome {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 16px;
  background: rgb(var(--v-theme-surface));
}

/* Mirrors Publish Flow's Run Summary (hero live count + five metric cards). */
.preview-run-summary-hero {
  display: flex;
  align-items: center;
  gap: 16px;
}

.preview-run-summary-count {
  min-width: 88px;
  font-size: 44px;
  line-height: 1;
  font-weight: 800;
  letter-spacing: -0.04em;
  color: rgb(var(--v-theme-on-surface));
}

.preview-run-summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 12px;
}

.preview-run-summary-metric {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 12px;
  background: rgb(var(--v-theme-surface));
}

.preview-run-summary-metric-value {
  font-size: 24px;
  line-height: 1.1;
  font-weight: 700;
  color: rgb(var(--v-theme-on-surface));
}

.preview-run-summary-metric-label {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.preview-run-summary-metric-action {
  margin-top: 8px;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  background: none;
  border: none;
  cursor: pointer;
}

.preview-run-summary-metric-action:hover {
  text-decoration: underline;
}

.preview-run-summary-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.preview-run-summary-meta-cell {
  min-width: 0;
}

.preview-run-summary-meta-cell--wide {
  grid-column: span 2;
}

.preview-run-summary-id {
  word-break: break-all;
  font-family: 'JetBrains Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', monospace;
  font-size: 12px;
}

/* Publish-Flow-style "Inventory outcome details" cards (Feed / Manual / Total). */
.preview-outcome-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

@media (max-width: 960px) {
  .preview-outcome-grid {
    grid-template-columns: 1fr;
  }
}

.preview-bucket-card {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 12px;
  background: rgb(var(--v-theme-surface));
  height: 100%;
}

.preview-bucket-card--emphasize {
  background: rgba(var(--v-theme-primary), 0.04);
  border-color: rgba(var(--v-theme-primary), 0.3);
}

.preview-bucket-card__title {
  font-size: 0.875rem;
  font-weight: 700;
}

.preview-bucket-card__subtitle {
  margin-bottom: 12px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

/* Three columns so the five metrics wrap into two rows, matching Publish
   Flow's ChangeBucketCard: row 1 Added/Removed/Changed, row 2 Made Visible/Hidden. */
.preview-bucket-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.preview-bucket-cell {
  min-width: 0;
}

.publish-change-summary__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  text-align: left;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 12px;
  background: rgb(var(--v-theme-surface));
}

.publish-change-summary__item--warning {
  border-color: rgba(var(--v-theme-warning), 0.48);
  background: rgba(var(--v-theme-warning), 0.08);
}

.publish-change-summary__item--danger {
  border-color: rgba(var(--v-theme-error), 0.5);
  background: rgba(var(--v-theme-error), 0.08);
}

.publish-change-summary__item:has(.publish-change-summary__action):hover {
  border-color: rgb(var(--v-theme-primary));
}

.publish-change-summary__label {
  font-weight: 700;
  line-height: 1.2;
}

.publish-change-summary__count {
  margin-top: 6px;
  color: rgb(var(--v-theme-primary));
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
}

.publish-change-summary__detail {
  margin-top: 4px;
  color: rgba(var(--v-theme-on-surface), 0.66);
  font-size: 12px;
}

.publish-change-summary__action {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: rgb(var(--v-theme-primary));
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.vin-lookup-panel {
  padding: 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.03);
}

.vin-preview-result-card {
  display: flex;
  flex-direction: column;
  max-height: min(92vh, 920px);
  overflow: hidden;
}

.vin-preview-result-card__header {
  flex: 0 0 auto;
  padding-top: 16px;
  padding-bottom: 12px;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.vin-preview-result-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 20px;
}

.vin-lookup-result__hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgba(var(--v-theme-primary), 0.04);
}

.vin-lookup-result__steps {
  display: grid;
  gap: 10px;
}

.vin-lookup-result__step {
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
}

.vin-lookup-result__changes {
  display: grid;
  gap: 8px;
}

.vin-lookup-result__change {
  display: grid;
  grid-template-columns: minmax(140px, 0.75fr) minmax(0, 1.25fr);
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.035);
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.vin-lookup-result__change-label {
  font-size: 13px;
  font-weight: 700;
}

.vin-lookup-result__change-values {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(var(--v-theme-on-surface), 0.72);
  font-size: 13px;
}

.preview-hero-summary {
  max-width: 860px;
  padding: 12px 14px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.04);
  color: rgb(var(--v-theme-on-surface));
  font-weight: 600;
}

.preview-flow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
  gap: 10px;
  max-width: 960px;
}

.preview-flow__step {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
  padding: 12px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
}

.preview-flow__step:hover {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.04);
}

.preview-flow__eyebrow {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(var(--v-theme-on-surface), 0.58);
  margin-bottom: 4px;
}

.preview-flow__label {
  font-weight: 700;
  line-height: 1.2;
}

.preview-flow__metric {
  margin-top: 8px;
  font-size: 18px;
  font-weight: 700;
  color: rgb(var(--v-theme-primary));
}

.preview-flow__detail {
  margin-top: 4px;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.66);
}

.preview-stage-row {
  scroll-margin-top: 96px;
}

.preview-stage-description {
  max-width: 760px;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.preview-stage-flow {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 8px;
  max-width: 760px;
}

.preview-stage-flow__item {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 10px 12px;
  background: rgba(var(--v-theme-surface-variant), 0.24);
}

.preview-stage-flow__label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.preview-stage-flow__value {
  margin-top: 2px;
  font-weight: 600;
}

.v-list-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.v-list-item:last-child {
  border-bottom: none;
}

.preprocessor-diagnostics {
  display: grid;
  gap: 12px;
  max-width: 760px;
}

.preprocessor-diagnostics__outcome {
  padding: 12px;
  border: 1px solid rgba(var(--v-theme-primary), 0.28);
  border-radius: 10px;
  background: rgba(var(--v-theme-primary), 0.05);
}

.preprocessor-diagnostics__eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(var(--v-theme-on-surface), 0.58);
}

.preprocessor-diagnostics__outcome-title {
  margin-top: 4px;
  color: rgb(var(--v-theme-primary));
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
}

.preprocessor-diagnostics__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}

.preprocessor-diagnostics__section {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 10px;
  padding: 12px;
  background: rgb(var(--v-theme-surface));
}

.preprocessor-diagnostics__title {
  font-weight: 700;
  margin-bottom: 10px;
}

.preprocessor-diagnostics__facts {
  display: grid;
  gap: 8px;
  margin: 0;
}

.preprocessor-diagnostics__facts > div {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.55);
}

.preprocessor-diagnostics__facts > div:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.preprocessor-diagnostics__facts dt {
  color: rgba(var(--v-theme-on-surface), 0.62);
}

.preprocessor-diagnostics__facts dd {
  margin: 0;
  text-align: right;
  font-weight: 600;
}

.preprocessor-diagnostics__files,
.preprocessor-diagnostics__subsection {
  margin-top: 12px;
}

.preprocessor-diagnostics__file-label,
.preprocessor-diagnostics__subheading {
  margin-bottom: 4px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  font-size: 12px;
  font-weight: 700;
}

.preprocessor-diagnostics__rule + .preprocessor-diagnostics__rule {
  margin-top: 4px;
}

.field-mapping-audit {
  max-width: 760px;
}

.field-mapping-audit__details {
  display: grid;
  gap: 8px;
}

.field-mapping-audit__section {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.015);
}

.field-mapping-audit__title {
  font-weight: 700;
  margin-bottom: 2px;
}

.vehicle-data-buckets {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.vehicle-data-bucket {
  width: 100%;
  text-align: left;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 12px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
}

.vehicle-data-bucket:hover,
.vehicle-data-bucket--selected {
  border-color: rgb(var(--v-theme-primary));
}

.vehicle-data-vin-list {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  overflow: hidden;
}

.vehicle-data-vin-row {
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  text-align: left;
  padding: 10px 12px;
  background: rgb(var(--v-theme-surface));
  border: 0;
  cursor: pointer;
}

.vehicle-data-vin-row + .vehicle-data-vin-row {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.vehicle-data-vin-row:hover,
.vehicle-data-vin-row--selected {
  background: rgba(var(--v-theme-primary), 0.06);
}

.vehicle-data-vin-detail {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  padding: 12px;
}

.feed-field-rules-summary {
  max-width: 760px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.015);
}

.group-bucket-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.vehicle-group-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  cursor: pointer;
}

.vehicle-group-row:hover {
  background: rgba(var(--v-theme-primary), 0.04);
}

.vehicle-group-detail {
  padding: 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}

.group-detail-card {
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  margin-top: 8px;
}

.listing-visibility-bucket-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.listing-visibility-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0;
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  cursor: pointer;
}

.listing-visibility-row:hover,
.listing-visibility-row--selected {
  background: rgba(var(--v-theme-primary), 0.04);
}

.listing-visibility-detail {
  padding: 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}

.listing-visibility-detail-card {
  padding: 12px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.vin-drilldown-layout {
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(320px, 1.1fr);
  gap: 16px;
}

.vin-drilldown-list,
.vin-drilldown-detail {
  min-height: 420px;
  max-height: 64vh;
  overflow: auto;
}

.vin-drilldown-detail {
  border-left: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-left: 16px;
}

.feed-intake-visibility-impact {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feed-intake-visibility-card {
  padding: 16px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 12px;
}

.feed-intake-visibility-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.feed-intake-visibility-counts {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 720px) {
  .vin-drilldown-layout {
    grid-template-columns: 1fr;
  }

  .vin-drilldown-detail {
    border-left: 0;
    border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    padding-top: 16px;
    padding-left: 0;
  }

  .vin-lookup-result__change {
    grid-template-columns: 1fr;
  }

  .feed-intake-visibility-grid {
    grid-template-columns: 1fr;
  }
}
</style>
